import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./lib/supabase";
import { addInstalments, deleteInstalment, updateInstalment, loadMyProfile, clearRecurringOverride, setRecurringOverride, setRecurringPaid, updateRecurringCost, loadSnapshots, storeForecastSnapshot, addDocSpend, updateDocSpend, setPaydownOverride, updateAmexPayment, deleteAmexPayment, addManualEntry, addUpdate, updateManualEntry, clearQuoteOverride, deleteDocSpend, loadAll, markDocPaid, saveExpectedDate, saveExpectedDates, saveOpeningPosition, saveQuoteOverride, unmarkDocPaid } from "./lib/data";
import syrupLogo from "./syrup-logo.svg";
import { buildTransactions, bucketByWeek } from "./engine/forecast";
import { addDays, daysLate, fmtDay, fmtMoney, fmtWeek, fyWeek, isoDate, parseDate, todayStr } from "./engine/dates";
import { T, fontDisplay, fontMono } from "./theme";
import AttachedSpend from "./views/AttachedSpend";
import ToCloseView from "./views/ToCloseView";
import AmexView from "./views/AmexView";
import BillsView from "./views/BillsView";
import ImportView from "./views/ImportView";
import QuotesView from "./views/QuotesView";
import RetainersView from "./views/RetainersView";
import CostsView from "./views/CostsView";
import QuestionsView from "./views/QuestionsView";
import AccuracyView from "./views/AccuracyView";
import ScenariosView from "./views/ScenariosView";
import AdminView from "./views/AdminView";
import OnAccountView from "./views/OnAccountView";


/**
 * Cost categories, matching v1. Costs group by category rather than by supplier:
 * thirty supplier rows is a list, seven category rows is a picture, and the
 * supplier detail is one click away rather than gone.
 */
/**
 * A path per tab, so a page can be refreshed, bookmarked or sent to someone.
 *
 * Deliberately readable rather than matching the internal tab names: nobody
 * should have to know the grid is called "cashflow" or the pipeline "quotes" to
 * make sense of a link.
 */
const TAB_PATHS = {
  cashflow: "/",
  retainers: "/retainers",
  quotes: "/pipeline",
  toclose: "/to-close",
  onaccount: "/money-in-out",
  costs: "/overheads",
  bills: "/bills",
  amex: "/amex",
  scenarios: "/scenarios",
  accuracy: "/accuracy",
  admin: "/admin",
  questions: "/questions",
  import: "/import",
};
const PATH_TABS = Object.fromEntries(Object.entries(TAB_PATHS).map(([k, v]) => [v, k]));

const tabFromPath = () => {
  if (typeof window === "undefined") return "cashflow";
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return PATH_TABS[path] || "cashflow";
};

const COST_CATEGORIES = [
  { id: "staff", label: "Staff" },
  { id: "rent", label: "Rent" },
  { id: "software", label: "Software" },
  { id: "suppliers", label: "Suppliers" },
  { id: "expenses", label: "Expenses" },
  { id: "media", label: "Media spend" },
  { id: "debt", label: "Debt" },
  { id: "tax", label: "Tax" },
  { id: "amex", label: "AMEX payment" },
  { id: "overheads", label: "Overheads" },
  { id: "other", label: "Other" },
];


/**
 * Each kind of document gets its own colour and label in the drawer.
 *
 * The drawer shows very different things through one frame — money owed, money
 * that has landed, a quote that may never happen — and they were separable only
 * by reading the small type at the top. A received payment and an unpaid invoice
 * looking alike is the confusion most worth removing, since one is a fact and
 * the other is a hope.
 */
const DOC_STYLE = {
  scoro_received: { label: "Received", accent: T.received, tint: T.receivedSoft, mark: "✓" },
  on_account_payment: { label: "Payment on account", accent: T.received, tint: T.receivedSoft, mark: "✓" },
  scoro_invoice: { label: "Invoice · awaiting payment", accent: T.income, tint: "#EDF3ED", mark: "●" },
  scoro_quote: { label: "Quote · pipeline", accent: T.pipeline, tint: "#F4F1FA", mark: "◇" },
  scoro_bill: { label: "Bill · to pay", accent: T.cost, tint: "#F7E9E9", mark: "●" },
  doc_spend: { label: "Attached cost", accent: T.cost, tint: "#F7E9E9", mark: "→" },
  amex_payment: { label: "AMEX payment", accent: T.cost, tint: "#F7E9E9", mark: "▣" },
  recurring: { label: "Recurring cost", accent: T.cost, tint: "#F7E9E9", mark: "↻" },
  manual_entry: { label: "Manual entry", accent: T.muted, tint: T.panelSoft, mark: "✎" },
  manual_entry_paid: { label: "Already paid", accent: T.settled, tint: T.settledSoft, mark: "✓" },
};

/**
 * The detail behind a figure.
 *
 * The point of v2: no number appears without the document behind it being one
 * click away — what it is, when it was due, how late, who owns it, and what
 * anyone has said about it since.
 */
function DocumentDrawer({ txn, updates, spend, plan, amexUnconfigured, linkedDoc, onOpenDoc, onClose, onBack, backCount, onSetExpected, onAddUpdate, onQuoteChange, onMarkPaid, onManualPaid, onManualEdit, onPlanChange, onPlanCreate, onSpendChanged, onRecurringChange, onRecurringPaid, relatedSpend, onMoveSpend, onPaydownChange, onAmexPaymentChange, session, readOnly = false }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [amountDraft, setAmountDraft] = useState("");
  const [dateDraft, setDateDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState(null);
  const [contactDraft, setContactDraft] = useState(null);

  // Something to save: a figure, a date, or wording that has actually moved.
  // Comparing the note against its current value rather than testing for text
  // means clearing a description counts as a change too.
  const src = txn?.source || {};
  const manualDirty =
    !!Number(amountDraft) ||
    !!dateDraft ||
    (noteDraft !== null && noteDraft !== (src.description ?? "")) ||
    (contactDraft !== null && !!contactDraft.trim() && contactDraft !== src.contact);
  const [planCount, setPlanCount] = useState(3);
  const [planStart, setPlanStart] = useState(todayStr());
  const [planEvery, setPlanEvery] = useState("month");
  if (!txn) return null;
  const s = txn.source || {};
  const late = s.dueDate ? daysLate(s.dueDate, todayStr()) : null;
  const styleKey =
    s.type === "manual_entry" && s.received && s.direction === "out" ? "manual_entry_paid" : s.type;
  // Only while the work is still to be won. Once the quote is completed the
  // job is happening and the cost is as certain as any other — calling it
  // conditional then overstates how much of the forecast is speculative.
  const conditional =
    s.type === "doc_spend" && s.attachedTo?.source === "scoro_quote" && !linkedDoc?.won;
  const style = conditional
    ? {
        label: "Attached cost · rides on a quote",
        accent: T.pipeline,
        tint: "#F4F1FA",
        mark: "◇",
      }
    : txn.isAwaitingPo
    ? // Agreed work waiting on a purchase order is not pipeline. Heading it
      // "Quote · pipeline" told the reader the opposite of what the stage says.
      { label: "Agreed · awaiting PO", accent: T.income, tint: "#EDF3ED", mark: "⬦" }
    : DOC_STYLE[styleKey] || {
        label: (s.type || "item").replace(/_/g, " "),
        accent: txn.amount >= 0 ? T.income : T.cost,
        tint: T.panelSoft,
        mark: "•",
      };

  const submit = async () => {
    if (!note.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onAddUpdate(s.type, s.id, note.trim());
      setNote("");
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ label, children }) =>
    children == null || children === "" ? null : (
      <div style={{ display: "flex", gap: 12, padding: "5px 0", fontSize: 12 }}>
        <div style={{ color: T.muted, minWidth: 130 }}>{label}</div>
        <div style={{ color: T.ink }}>{children}</div>
      </div>
    );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(26,24,21,0.35)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 50,
        cursor: "pointer",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(460px, 100%)",
          background: T.panel,
          height: "100%",
          cursor: "auto",
          overflowY: "auto",
          padding: 20,
          boxShadow: "-8px 0 24px rgba(0,0,0,0.12)",
        }}
      >
        <div
          style={{
            margin: "-20px -20px 0",
            padding: "16px 20px",
            background: style.tint,
            borderTop: `4px solid ${style.accent}`,
          }}
        >
          {/* A week can hold several documents. Arriving at one of them from
              that list and finding only a close button means going back out to
              the grid and starting again to read the next one. */}
          {onBack && (
            <button
              onClick={onBack}
              style={{
                border: "none",
                background: "none",
                padding: 0,
                marginBottom: 10,
                color: T.muted,
                fontSize: 11,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              ‹ Back to {backCount ? `all ${backCount}` : "the list"}
            </button>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
            <div>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 0.8,
                  color: style.accent,
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                {style.mark} {style.label}
              </div>
              <h2 style={{ fontFamily: fontDisplay, fontSize: 22, margin: "3px 0 0" }}>{txn.label}</h2>
            </div>
            <button
              onClick={onClose}
              style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: T.muted }}
            >
              ×
            </button>
          </div>

          <div style={{ fontFamily: fontMono, fontSize: 26, marginTop: 10, color: style.accent }}>
            {fmtMoney(txn.amount)}
          </div>
          {s.paidDate && (
            <div style={{ fontSize: 11, color: style.accent, marginTop: 2, fontWeight: 600 }}>
              In the bank {fmtDay(s.paidDate)}
            </div>
          )}
          {s.partOf && (
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
              {s.partOf} against {s.docNo} · {fmtMoney(s.gross)} in total
            </div>
          )}
        </div>

        <div style={{ height: 12 }} />

        <div style={{ borderTop: `1px solid ${T.borderSoft}`, paddingTop: 8 }}>
          <Row label="Document">{s.docNo}</Row>
          <Row label="Contact">{s.contact}</Row>
          <Row label="Description">{s.description}</Row>
          {s.rolledForward && (
            <Row label="Dated">
              <span style={{ color: T.warn }}>
                {fmtDay(s.originalDate)} — before the opening balance and not marked paid, so it is
                shown as still to go rather than dropped
              </span>
            </Row>
          )}
          <Row label="Received">{s.paidDate ? fmtDay(s.paidDate) : null}</Row>
          <Row label="Issued">{s.issued ? fmtDay(s.issued) : null}</Row>
          <Row label="Due">
            {s.dueDate ? (
              <span>
                {fmtDay(s.dueDate)}{" "}
                {late != null && (
                  <span
                    style={{
                      color: s.paidDate ? T.muted : late > 0 ? T.cost : T.muted,
                      fontWeight: late > 0 && !s.paidDate ? 600 : 400,
                    }}
                  >
                    {" · "}
                    {s.paidDate
                      ? late > 0
                        ? `paid ${late} days after it was due`
                        : "paid on time"
                      : late > 0
                        ? `${late} days late`
                        : late === 0
                          ? "due today"
                          : `due in ${-late} days`}
                  </span>
                )}
              </span>
            ) : null}
          </Row>
          <Row label="Invoice total">{s.gross ? fmtMoney(s.gross) : null}</Row>
          <Row label="Billed in">
            {s.foreign
              ? `${s.foreign.currency} ${Math.round(s.foreign.outstanding).toLocaleString()} · converted at ${s.foreign.rate}`
              : null}
          </Row>
          <Row label="Already paid">{s.paid ? fmtMoney(s.paid) : null}</Row>
          <Row label="Still outstanding">{s.outstanding ? fmtMoney(s.outstanding) : null}</Row>
          <Row label="Stage">
            {s.stage ? (
              <span
                style={{
                  background: s.stageColour || T.borderSoft,
                  borderRadius: 999,
                  padding: "2px 9px",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                {s.stage}
              </span>
            ) : null}
          </Row>
          <Row label="Quote">{s.quoteName}</Row>
          <Row label="Est. closing">{s.estimatedClosing ? fmtDay(s.estimatedClosing) : null}</Row>
          <Row label="Statement date">{s.statementDate ? fmtDay(s.statementDate) : null}</Row>
          <Row label="Frequency">{s.frequency}</Row>
          <Row label="From">{s.note}</Row>
        </div>

        {/* Won work has an invoice; the invoice carries the money now.
            Offering to add the quote to the forecast here is offering to count
            it twice, which is exactly what happened to Bighams. */}
        {s.type === "scoro_quote" && s.statusKey === "completed" && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${T.settled}`,
              borderRadius: 8,
              padding: 12,
              background: T.settledSoft,
            }}
          >
            <div style={{ fontSize: 11, color: T.settled, fontWeight: 600, marginBottom: 6 }}>
              Won — this became an invoice
            </div>
            <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
              The invoice carries the money now, so the quote is deliberately not in the forecast —
              counting both would count the work twice. Any costs attached to it are still
              included: winning the work is what made them certain.
            </div>
          </div>
        )}

        {s.type === "scoro_quote" && s.statusKey !== "completed" && !readOnly && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${T.pipeline}`,
              borderRadius: 8,
              padding: 12,
              background: "#F4F1FA",
            }}
          >
            <div style={{ fontSize: 11, color: T.pipeline, fontWeight: 600, marginBottom: 8 }}>
              In the cash forecast
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 8, lineHeight: 1.5 }}>
              {s.inForecast
                ? `Included by ${s.includedBy}.`
                : "Not in the forecast yet. Set when it should land and what it costs, then add it."}
            </div>

            {/* Say plainly what pressing the button will do to the forecast. */}
            <div
              style={{
                fontSize: 11,
                background: "#fff",
                border: `1px solid ${T.borderSoft}`,
                borderRadius: 6,
                padding: "8px 10px",
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: T.muted }}>Adds to the forecast</span>
                <span style={{ fontFamily: fontMono, color: T.pipeline, fontWeight: 600 }}>
                  {fmtMoney(((s.fullValue || 0) * (s.probability ?? 100)) / 100)}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                <span style={{ color: T.muted }}>Landing</span>
                <span style={{ fontFamily: fontMono }}>
                  {s.expectedDate ? fmtDay(s.expectedDate) : "—"}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <button
                onClick={() => onQuoteChange(s.id, { include: !s.inForecast, probability: s.probability ?? 100 })}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: s.inForecast ? T.border : T.pipeline,
                  color: s.inForecast ? T.ink : "#fff",
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {s.inForecast ? "Remove from forecast" : "Add to forecast"}
              </button>
              <label style={{ fontSize: 10, color: T.muted }}>
                confidence
                <br />
                <input
                  type="number"
                  min="0"
                  max="100"
                  defaultValue={s.probability ?? 100}
                  onBlur={(e) => {
                    const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                    if (v !== Number(s.probability ?? 100)) {
                      onQuoteChange(s.id, { include: true, probability: v });
                    }
                  }}
                  style={{ width: 56, fontFamily: fontMono, fontSize: 11, padding: "3px 4px" }}
                />
                %
              </label>
              <label style={{ fontSize: 10, color: T.muted }}>
                when it should land
                <br />
                <input
                  type="date"
                  key={`q-${s.id}-${s.expectedDate}`}
                  defaultValue={s.expectedDate || ""}
                  min="2000-01-01"
                  max="2099-12-31"
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v && v !== s.expectedDate && /^(20|21)\d{2}-\d{2}-\d{2}$/.test(v)) {
                      onQuoteChange(s.id, { include: true, expected_date: v });
                    }
                  }}
                  style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 4px" }}
                />
              </label>
            </div>
            {s.fullValue != null && (
              <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>
                {fmtMoney(s.fullValue)} quoted
                {s.probability != null && s.probability !== 100 && s.inForecast
                  ? ` · ${fmtMoney((s.fullValue * s.probability) / 100)} in the forecast at ${s.probability}%`
                  : ""}
              </div>
            )}
          </div>
        )}

        {s.type !== "scoro_quote" && !s.onPaymentPlan && s.expectedDate && s.type !== "manual_entry" && !readOnly && (
          <div style={{ marginTop: 12, borderTop: `1px solid ${T.borderSoft}`, paddingTop: 12 }}>
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>
              {txn.amount >= 0 ? "Expected in the bank" : "Expected to leave the bank"}{" "}
              {s.expectedIsOverride ? "(set by hand)" : "(Friday rule)"}
            </div>
            <input
              type="date"
              // Uncontrolled and saved on blur: a date input reports a complete
              // date after every keystroke, so saving per change stores the
              // half-typed one.
              key={`${s.type}-${s.id}-${s.expectedDate}`}
              defaultValue={s.expectedDate}
              min="2000-01-01"
              max="2099-12-31"
              onBlur={(e) => {
                const v = e.target.value;
                if (v && v !== s.expectedDate && /^(20|21)\d{2}-\d{2}-\d{2}$/.test(v)) {
                  onSetExpected(s.type, s.id, v);
                }
              }}
              style={{
                fontFamily: fontMono,
                fontSize: 12,
                padding: "4px 6px",
                border: `1px solid ${s.expectedIsOverride ? T.accent : T.border}`,
                borderRadius: 4,
                background: s.expectedIsOverride ? T.accentSoft : "transparent",
              }}
            />
            {s.expectedIsOverride && (
              <button
                onClick={() => onSetExpected(s.type, s.id, null)}
                style={{
                  marginLeft: 8,
                  border: "none",
                  background: "none",
                  color: T.muted,
                  fontSize: 11,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                reset
              </button>
            )}
          </div>
        )}

        {/* A cost exists because a job does. Naming the job — and whether that
            job is itself certain — is the difference between a number and a
            reason for a number. */}
        {s.attachedTo && linkedDoc && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${linkedDoc.isPipeline ? T.pipeline : T.income}`,
              borderRadius: 8,
              padding: 12,
              background: linkedDoc.isPipeline ? "#F4F1FA" : "#EDF3ED",
            }}
          >
            <div
              style={{
                fontSize: 10,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                fontWeight: 700,
                color: linkedDoc.isPipeline ? T.pipeline : T.income,
                marginBottom: 6,
              }}
            >
              {linkedDoc.isAwaitingPo
                ? "⬦ Delivers agreed work · awaiting PO"
                : linkedDoc.isPipeline
                  ? "◇ Delivers work not yet won"
                  : "● Delivers invoiced work"}
            </div>
            <button
              onClick={() => linkedDoc.txn && onOpenDoc?.(linkedDoc.txn)}
              disabled={!linkedDoc.txn}
              style={{
                border: "none",
                background: "none",
                padding: 0,
                textAlign: "left",
                cursor: onOpenDoc ? "pointer" : "default",
                font: "inherit",
                width: "100%",
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600 }}>{linkedDoc.label}</div>
              <div style={{ fontFamily: fontMono, fontSize: 17, marginTop: 2 }}>
                {fmtMoney(linkedDoc.amount)}
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
                {linkedDoc.docNo ? `${linkedDoc.docNo} · ` : ""}
                expected {fmtDay(linkedDoc.date)}
                {linkedDoc.stage ? ` · ${linkedDoc.stage}` : ""}
                {linkedDoc.won
                  ? " · won, so the invoice carries the income now"
                  : linkedDoc.notForecast
                    ? " · not in the forecast itself"
                    : ""}
              </div>
            </button>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
              {linkedDoc.isPipeline && !linkedDoc.isAwaitingPo ? (
                <>
                  <strong style={{ color: T.ink }}>If this quote does not land, this cost does
                  not happen.</strong>{" "}
                  It leaves the forecast with the quote.
                </>
              ) : (
                <>
                  Margin on this piece of work:{" "}
                  <strong style={{ color: T.ink }}>
                    {fmtMoney(linkedDoc.amount / 1.2 - (s.netAmount ?? Math.abs(txn.amount)))}
                  </strong>{" "}
                  excl. VAT.
                </>
              )}
            </div>
          </div>
        )}

        {/* A standing cost is a pattern; one month being different is a fact
            about that month. Both edits are offered, because conflating them
            either loses the exception or quietly rewrites the pattern. */}
        {s.type === "recurring" && !readOnly && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              padding: 12,
              background: T.panelSoft,
            }}
          >
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 8 }}>
              Change the amount
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                autoFocus
                value={amountDraft}
                onChange={(e) => setAmountDraft(e.target.value)}
                placeholder={String(Math.round(Math.abs(txn.amount)))}
                style={{
                  fontFamily: fontMono,
                  fontSize: 14,
                  padding: "6px 8px",
                  width: 120,
                  borderRadius: 6,
                  border: `1px solid ${T.border}`,
                }}
              />
              <button
                disabled={busy || !Number(amountDraft)}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await onRecurringChange("once", s.id, s.occurrenceDate, Number(amountDraft));
                  } catch (e) {
                    setError(e.message || String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: Number(amountDraft) ? T.accent : T.border,
                  color: Number(amountDraft) ? "#fff" : T.muted,
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                just {fmtDay(s.occurrenceDate)}
              </button>
              <button
                disabled={busy || !Number(amountDraft)}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await onRecurringChange("always", s.id, s.occurrenceDate, Number(amountDraft));
                  } catch (e) {
                    setError(e.message || String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: `1px solid ${T.border}`,
                  background: "transparent",
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                every {s.frequency === "monthly" ? "month" : s.frequency?.replace(/ly$/, "")}
              </button>
              {s.adjusted && (
                <button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await onRecurringChange("reset", s.id, s.occurrenceDate);
                    } catch (e) {
                      setError(e.message || String(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  style={{
                    border: "none",
                    background: "none",
                    color: T.muted,
                    fontSize: 11,
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontFamily: "inherit",
                  }}
                >
                  back to {fmtMoney(s.standardAmount)}
                </button>
              )}
            </div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
              {s.adjusted
                ? `Normally ${fmtMoney(s.standardAmount)} — this one has been changed.`
                : `Normally ${fmtMoney(s.standardAmount)} ${s.frequency === "monthly" ? "a month" : `per ${s.frequency}`}.`}
            </div>
            {/* Reconciling a week that has been: this one actually went out.
                A single line here so a bulk tick made in error has somewhere
                obvious to be undone. */}
            {!readOnly && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: T.muted }}>
                  {txn.isHistory
                    ? "Already inside the opening balance"
                    : s.paidDate
                      ? "Marked as gone out"
                      : "Not marked as paid"}
                </span>
                <button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await onRecurringPaid(s.id, s.occurrenceDate, !s.paidDate);
                    } catch (e) {
                      setError(e.message || String(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  style={{
                    padding: "4px 11px",
                    borderRadius: 6,
                    border: s.paidDate ? `1px solid ${T.border}` : "none",
                    background: s.paidDate ? "transparent" : T.accent,
                    color: s.paidDate ? T.muted : "#fff",
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {s.paidDate ? "not paid after all" : "Mark it paid"}
                </button>
                {/* The budget line and the real bill are the same money.
                    A standing cost is an estimate of what a category costs;
                    when the actual invoice arrives and is paid through
                    suppliers, the estimate has done its job and counting both
                    forecasts the spend twice. Professional fees was Sales with
                    Soul and Responsa, already paid as bills. */}
                <button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await onRecurringChange("skip", s.id, s.occurrenceDate);
                    } catch (e) {
                      setError(e.message || String(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  title="A real invoice covers this month — take the estimate out so it is not counted twice"
                  style={{
                    padding: "4px 11px",
                    borderRadius: 6,
                    border: `1px solid ${T.border}`,
                    background: "transparent",
                    color: T.muted,
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    marginLeft: "auto",
                  }}
                >
                  a bill covers this — remove it
                </button>
              </div>
            )}
          </div>
        )}

        {/* A manual entry is a figure somebody typed, so it is the one most
            likely to need correcting and the one with no document to correct it
            from. Amount and date are editable here. */}
        {s.type === "manual_entry" && !readOnly && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              padding: 12,
              background: T.panelSoft,
            }}
          >
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 8 }}>
              Change this entry
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                value={amountDraft}
                onChange={(e) => setAmountDraft(e.target.value)}
                placeholder={String(Math.round(Math.abs(txn.amount)))}
                style={{
                  fontFamily: fontMono,
                  fontSize: 14,
                  padding: "6px 8px",
                  width: 120,
                  borderRadius: 6,
                  border: `1px solid ${T.border}`,
                }}
              />
              <input
                type="date"
                value={dateDraft || s.expectedDate || ""}
                onChange={(e) => setDateDraft(e.target.value)}
                style={{
                  fontFamily: fontMono,
                  fontSize: 12,
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: `1px solid ${T.border}`,
                }}
              />
              <input
                value={contactDraft ?? s.contact ?? ""}
                onChange={(e) => setContactDraft(e.target.value)}
                placeholder="Who"
                style={{
                  fontFamily: "inherit",
                  fontSize: 12,
                  padding: "6px 8px",
                  width: 150,
                  borderRadius: 6,
                  border: `1px solid ${T.border}`,
                }}
              />
            </div>
            <textarea
              value={noteDraft ?? s.description ?? ""}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="What this is, and why it is here"
              rows={2}
              style={{
                fontFamily: "inherit",
                fontSize: 12,
                lineHeight: 1.5,
                padding: "6px 8px",
                width: "100%",
                boxSizing: "border-box",
                marginTop: 8,
                borderRadius: 6,
                border: `1px solid ${T.border}`,
                resize: "vertical",
              }}
            />
            <div style={{ marginTop: 8 }}>
              <button
                disabled={busy || !manualDirty}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    const patch = {};
                    if (Number(amountDraft)) patch.amount = Math.abs(Number(amountDraft));
                    if (dateDraft) patch.entry_date = dateDraft;
                    // Compared against what is there, not merely non-empty, so
                    // a description can be cleared as well as rewritten.
                    if (noteDraft !== null && noteDraft !== (s.description ?? "")) {
                      patch.note = noteDraft.trim() || null;
                    }
                    if (contactDraft !== null && contactDraft.trim() && contactDraft !== s.contact) {
                      patch.label = contactDraft.trim();
                    }
                    await onManualEdit(s.id, patch);
                    setNoteDraft(null);
                    setContactDraft(null);
                    setAmountDraft("");
                    setDateDraft("");
                  } catch (e) {
                    setError(e.message || String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "none",
                  background: manualDirty ? T.accent : T.border,
                  color: manualDirty ? "#fff" : T.muted,
                  fontSize: 11,
                  cursor: manualDirty ? "pointer" : "default",
                  fontFamily: "inherit",
                }}
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 8 }}>
              Currently {fmtMoney(txn.amount)}
              {s.expectedDate ? ` on ${fmtDay(s.expectedDate)}` : ""}. Every change is recorded in
              the audit trail.
            </div>
          </div>
        )}

        {s.type === "manual_entry" && !readOnly && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 8,
              background: s.received ? T.settledSoft : T.panelSoft,
              border: `1px solid ${s.received ? T.settled : T.border}`,
              fontSize: 11,
            }}
          >
            {s.received ? (
              <span style={{ color: T.settled, fontWeight: 600 }}>
                ✓ {s.direction === "out" ? "Already paid" : "Already received"}
              </span>
            ) : (
              <span style={{ color: T.muted }}>
                Not yet {s.direction === "out" ? "paid" : "received"}
              </span>
            )}
            <button
              onClick={() => onManualPaid(s.id, !s.received)}
              style={{
                marginLeft: 10,
                border: "none",
                background: s.received ? "transparent" : T.accent,
                color: s.received ? T.muted : "#fff",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                cursor: "pointer",
                fontFamily: "inherit",
                textDecoration: s.received ? "underline" : "none",
              }}
            >
              {s.received
                ? "undo"
                : `Mark it ${s.direction === "out" ? "paid" : "received"}`}
            </button>
          </div>
        )}

        {(s.type === "scoro_bill" || s.type === "scoro_invoice") && !s.onPaymentPlan && !readOnly && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 8,
              background: s.paidDate ? T.settledSoft : T.panelSoft,
              border: `1px solid ${s.paidDate ? T.settled : T.border}`,
            }}
          >
            {s.paidDate ? (
              <div style={{ fontSize: 11 }}>
                <strong style={{ color: T.settled }}>
                  ✓ {s.type === "scoro_bill" ? "Paid" : "Received"} {fmtDay(s.paidDate)}
                </strong>
                {s.markedByHand && (
                  <span style={{ color: T.muted }}> · recorded by hand, ahead of Scoro</span>
                )}
                <button
                  onClick={() => onMarkPaid(s.type, s.id, null)}
                  style={{
                    marginLeft: 8,
                    border: "none",
                    background: "none",
                    color: T.muted,
                    fontSize: 11,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  undo
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: T.muted }}>
                  {s.type === "scoro_bill" ? "Already paid?" : "Already received?"}
                </span>
                <input
                  type="date"
                  defaultValue={todayStr()}
                  id={`paid-${s.id}`}
                  style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 5px" }}
                />
                <button
                  onClick={() =>
                    onMarkPaid(s.type, s.id, document.getElementById(`paid-${s.id}`)?.value || todayStr())
                  }
                  style={{
                    border: "none",
                    background: T.accent,
                    color: "#fff",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Mark it {s.type === "scoro_bill" ? "paid" : "received"}
                </button>
                <span style={{ fontSize: 10, color: T.muted }}>
                  until Xero is connected
                </span>
              </div>
            )}
          </div>
        )}

        {/* A plan can only be edited if one exists, and until now nothing could
            create one. A supplier agreeing to be paid in instalments is an
            ordinary event; it should not need a script. */}
        {(s.type === "scoro_bill" || s.type === "scoro_invoice") &&
          !plan?.length &&
          !readOnly &&
          s.outstanding > 0 && (
            <div
              style={{
                marginTop: 12,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: 12,
                background: T.panelSoft,
              }}
            >
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 8 }}>
                Agreed to pay this in instalments?
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <label style={{ fontSize: 10, color: T.muted }}>
                  How many
                  <br />
                  <input
                    type="number"
                    min="2"
                    max="24"
                    value={planCount}
                    onChange={(e) => setPlanCount(e.target.value)}
                    style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 56, marginTop: 2 }}
                  />
                </label>
                <label style={{ fontSize: 10, color: T.muted }}>
                  First one
                  <br />
                  <input
                    type="date"
                    value={planStart}
                    onChange={(e) => setPlanStart(e.target.value)}
                    style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", marginTop: 2 }}
                  />
                </label>
                <label style={{ fontSize: 10, color: T.muted }}>
                  Then every
                  <br />
                  <select
                    value={planEvery}
                    onChange={(e) => setPlanEvery(e.target.value)}
                    style={{ fontFamily: "inherit", fontSize: 12, padding: "5px 6px", marginTop: 2 }}
                  >
                    <option value="week">week</option>
                    <option value="fortnight">fortnight</option>
                    <option value="month">month</option>
                  </select>
                </label>
                <button
                  disabled={busy || Number(planCount) < 2 || !planStart}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await onPlanCreate(s.type, s.id, {
                        count: Number(planCount),
                        start: planStart,
                        every: planEvery,
                        total: s.outstanding,
                      });
                    } catch (e) {
                      setError(e.message || String(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 6,
                    border: "none",
                    background: T.accent,
                    color: "#fff",
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {busy ? "Setting up…" : "Split it"}
                </button>
              </div>
              <div style={{ fontSize: 10, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
                {fmtMoney(s.outstanding)} over {planCount || 0} payments is about{" "}
                {fmtMoney(Number(planCount) ? s.outstanding / Number(planCount) : 0)} each. Every
                date and amount stays editable afterwards, so an uneven plan is a matter of
                adjusting them.
              </div>
            </div>
          )}

        {plan?.length > 1 && (
          <div
            style={{
              marginTop: 14,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              padding: 12,
              background: T.panelSoft,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>
              Payment plan · {plan.length} payments
            </div>
            <div style={{ fontSize: 10, color: T.muted, marginBottom: 8 }}>
              This document is forecast by these payments instead of its balance, so the two can
              never both be counted. Change a date or an amount here — the document's own date is
              ignored while a plan exists, which is why editing it does nothing.
            </div>
            {plan.map((it) => {
              const isThis = String(it.due_date) === String(s.expectedDate);
              return (
                <div
                  key={it.id}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "baseline",
                    padding: "5px 6px",
                    borderRadius: 4,
                    fontSize: 11,
                    background: isThis ? "#fff" : "transparent",
                    border: isThis ? `1px solid ${T.border}` : "1px solid transparent",
                  }}
                >
                  {readOnly ? (
                    <span style={{ fontFamily: fontMono, color: T.muted, minWidth: 78 }}>
                      {fmtDay(it.due_date)}
                    </span>
                  ) : (
                    <input
                      type="date"
                      key={`pd-${it.id}-${it.due_date}`}
                      defaultValue={it.due_date}
                      onBlur={(e) => {
                        const v = e.target.value;
                        if (v && v !== it.due_date) onPlanChange?.("date", it.id, v);
                      }}
                      style={{
                        fontFamily: fontMono,
                        fontSize: 11,
                        padding: "2px 4px",
                        border: `1px solid ${T.border}`,
                        borderRadius: 4,
                        background: "transparent",
                      }}
                    />
                  )}
                  {readOnly ? (
                    <span style={{ fontFamily: fontMono, color: it.paid ? T.settled : T.cost, minWidth: 66 }}>
                      {fmtMoney(-it.amount)}
                    </span>
                  ) : (
                    <input
                      key={`pa-${it.id}-${it.amount}`}
                      defaultValue={Math.round(it.amount)}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v && v !== Number(it.amount)) onPlanChange?.("amount", it.id, v);
                      }}
                      style={{
                        fontFamily: fontMono,
                        fontSize: 11,
                        padding: "2px 4px",
                        width: 68,
                        textAlign: "right",
                        border: `1px solid ${T.border}`,
                        borderRadius: 4,
                        background: "transparent",
                        color: it.paid ? T.settled : T.cost,
                      }}
                    />
                  )}
                  {readOnly ? (
                    <span style={{ fontSize: 10, color: it.paid ? T.settled : T.muted }}>
                      {it.paid ? "✓ paid" : "due"}
                    </span>
                  ) : (
                    <label style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, color: it.paid ? T.settled : T.muted }}>
                      <input
                        type="checkbox"
                        checked={!!it.paid}
                        onChange={(e) => onPlanChange?.("paid", it.id, e.target.checked)}
                      />
                      paid
                    </label>
                  )}
                  {it.note && (
                    <span style={{ fontSize: 10, color: T.muted }}>{it.note}</span>
                  )}
                  <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                    {isThis && (
                      <span style={{ fontSize: 9, color: T.ink, fontWeight: 700 }}>THIS ONE</span>
                    )}
                    {!readOnly && (
                      <button
                        onClick={() => onPlanChange?.("remove", it.id)}
                        title="Remove this payment from the plan"
                        style={{ border: "none", background: "none", color: T.muted, cursor: "pointer", fontSize: 13, lineHeight: 1 }}
                      >
                        ×
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: `1px solid ${T.borderSoft}`,
                marginTop: 6,
                paddingTop: 5,
                fontSize: 11,
              }}
            >
              <span style={{ color: T.muted }}>Plan total</span>
              <span style={{ fontFamily: fontMono }}>
                {fmtMoney(-plan.reduce((a, b) => a + Number(b.amount), 0))}
                {s.outstanding != null && (
                  <span style={{ color: T.muted }}>
                    {" "}
                    of {fmtMoney(s.outstanding)} outstanding
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Costs that belong to this work but hang off a different document.
            A quote becomes an invoice and the costs stay attached to the quote,
            which is right — they were agreed against it — but it leaves the
            invoice looking as though nothing has been budgeted for delivering
            it. They are shown here rather than moved automatically: a project
            can carry five invoices, and picking one would be a guess. */}
        {/* A paydown the limit forced, not a statement falling due.
            It is the engine saying "the card would go over, so this much has to
            leave" — a calculation, not a record, which is why there was nothing
            here to edit. Refusing it is a real answer: it leaves the card over
            its limit in the forecast, which is the true consequence rather than
            a tidier number. */}
        {/* A payment somebody recorded, as opposed to one the limit forced.
            It had no controls at all, so a £13,940 line entered once as a
            suggestion sat in the forecast permanently — the override mechanism
            cannot touch a stored record, and there was no way to reach it from
            the week it appeared in. */}
        {s.type === "amex_payment" && s.manual && !s.forced && !readOnly && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              padding: 12,
              background: T.panelSoft,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Change this payment</div>
            <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.6, marginBottom: 8 }}>
              A payment somebody entered, not a statement falling due. If it was wrong, or it is not
              going to happen, change it here.
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                id={`amexpay-amt-${s.id}`}
                defaultValue={Math.round(Math.abs(txn.amount))}
                style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 7px", width: 110, borderRadius: 6, border: `1px solid ${T.border}` }}
              />
              <input
                type="date"
                id={`amexpay-date-${s.id}`}
                defaultValue={s.paymentDate || isoDate(txn.date)}
                style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 7px", borderRadius: 6, border: `1px solid ${T.border}` }}
              />
              <button
                disabled={busy}
                onClick={async () => {
                  const amt = Number(document.getElementById(`amexpay-amt-${s.id}`)?.value);
                  const when = document.getElementById(`amexpay-date-${s.id}`)?.value;
                  if (!amt || !when) return;
                  setBusy(true);
                  setError(null);
                  try {
                    await onAmexPaymentChange("update", s.paymentId, { amount: amt, payment_date: when });
                  } catch (e) {
                    setError(e.message || String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: T.accent, color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
              >
                Save
              </button>
              <button
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await onAmexPaymentChange("delete", s.paymentId);
                  } catch (e) {
                    setError(e.message || String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.cost}`, background: "transparent", color: T.cost, fontSize: 11, cursor: "pointer", fontFamily: "inherit", marginLeft: "auto" }}
              >
                This did not happen
              </button>
            </div>
          </div>
        )}

        {s.type === "amex_payment" && s.forced && !readOnly && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${T.warn}`,
              borderRadius: 8,
              padding: 12,
              background: "#FBEFE2",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
              This is not a statement — the limit forced it
            </div>
            <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.6, marginBottom: 8 }}>
              Without it the card would reach {fmtMoney(s.wouldReach || 0)} against a limit of{" "}
              {fmtMoney(s.limit || 0)}. If this did not happen, say so — the forecast will show the
              card over its limit, which is what would actually be true.
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                id={`paydown-${s.paydownDate}`}
                defaultValue={Math.round(Math.abs(txn.amount))}
                style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 7px", width: 110, borderRadius: 6, border: `1px solid ${T.border}` }}
              />
              <button
                disabled={busy}
                onClick={async () => {
                  const v = Number(document.getElementById(`paydown-${s.paydownDate}`)?.value);
                  if (!v && v !== 0) return;
                  setBusy(true);
                  setError(null);
                  try {
                    await onPaydownChange(s.paydownDate, { amount: v, skip: false });
                  } catch (e) {
                    setError(e.message || String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: T.accent, color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
              >
                Change the amount
              </button>
              <button
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await onPaydownChange(s.paydownDate, { amount: null, skip: true });
                  } catch (e) {
                    setError(e.message || String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.cost}`, background: "transparent", color: T.cost, fontSize: 11, cursor: "pointer", fontFamily: "inherit", marginLeft: "auto" }}
              >
                This did not happen
              </button>
            </div>
          </div>
        )}

        {s.type === "scoro_invoice" && !!relatedSpend?.length && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${T.pipeline}`,
              borderRadius: 8,
              padding: 12,
              background: "#F4F1FA",
            }}
          >
            <div style={{ fontSize: 11, color: T.pipeline, fontWeight: 600, marginBottom: 6 }}>
              Spend on this project, attached elsewhere
            </div>
            <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.6, marginBottom: 8 }}>
              Already in the forecast, against the quote it was agreed on. Move it here if this is
              the invoice it belongs to.
            </div>
            {relatedSpend.map((r) => (
              <div
                key={r.id}
                style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}
              >
                <span style={{ fontFamily: fontMono, fontSize: 12, color: T.cost }}>
                  {fmtMoney(-Math.abs(Number(r.amount) || 0))}
                </span>
                <span style={{ fontSize: 12 }}>{r.supplier_name || r.description}</span>
                <span style={{ fontSize: 11, color: T.muted }}>
                  on quote {r.quoteNo}
                  {r.method === "amex" ? " · on the card" : ""}
                  {r.spread_weeks > 1 ? ` · over ${r.spread_weeks} weeks` : ""}
                  {r.start_date ? ` from ${fmtDay(r.start_date)}` : ""}
                </span>
                {!readOnly && (
                  <button
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      setError(null);
                      try {
                        await onMoveSpend(r.id, s.type, s.id);
                      } catch (e) {
                        setError(e.message || String(e));
                      } finally {
                        setBusy(false);
                      }
                    }}
                    style={{
                      marginLeft: "auto",
                      padding: "3px 10px",
                      borderRadius: 5,
                      border: `1px solid ${T.pipeline}`,
                      background: "transparent",
                      color: T.pipeline,
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    move to this invoice
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Income typed in gets the same treatment as a document. It is the
            income whose delivery cost is least likely to be recorded anywhere
            else — there is no quote carrying it and no supplier invoice yet —
            so leaving it out meant the lines most in need of a cost were the
            only ones that could not have one. */}
        {(s.type === "scoro_invoice" || s.type === "scoro_quote" || (s.type === "manual_entry" && s.direction === "in")) && (
          <AttachedSpend
            readOnly={readOnly}
            docSource={s.type}
            docId={s.id}
            income={s.type === "scoro_quote" ? s.fullValue : s.type === "manual_entry" ? Math.abs(txn.amount) : s.outstanding}
            netIncome={s.netOutstanding ?? txn.net ?? null}
            spend={spend}
            amexUnconfigured={amexUnconfigured}
            onChanged={onSpendChanged}
          />
        )}

        {s.source?.attachedTo && null}

        <div style={{ marginTop: 16, borderTop: `1px solid ${T.borderSoft}`, paddingTop: 12 }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 6 }}>
            Updates · never edited or deleted, so the trail stays honest
          </div>
          {readOnly ? null : (
          <>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Chased Jo on 3 Sept, says payment run is the 12th…"
            rows={2}
            style={{
              width: "100%",
              fontFamily: "inherit",
              fontSize: 12,
              padding: 8,
              border: `1px solid ${T.border}`,
              borderRadius: 6,
              resize: "vertical",
            }}
          />
          {error && <div style={{ color: T.cost, fontSize: 11, marginTop: 4 }}>{error}</div>}
          <button
            onClick={submit}
            disabled={busy || !note.trim()}
            style={{
              marginTop: 6,
              padding: "5px 12px",
              fontSize: 11,
              borderRadius: 6,
              border: "none",
              background: note.trim() ? T.accent : T.border,
              color: note.trim() ? "#fff" : T.muted,
              cursor: note.trim() ? "pointer" : "default",
            }}
          >
            {busy ? "Saving…" : "Add update"}
          </button>
          </>
          )}

          <div style={{ marginTop: 12 }}>
            {(updates || []).map((u) => (
              <div key={u.id} style={{ borderTop: `1px solid ${T.borderSoft}`, padding: "8px 0" }}>
                <div style={{ fontSize: 12, color: T.ink, whiteSpace: "pre-wrap" }}>{u.note}</div>
                <div style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>
                  {u.author_email || "someone"} · {fmtDay(u.created_at)}
                </div>
              </div>
            ))}
            {!(updates || []).length && (
              <div style={{ fontSize: 11, color: T.muted }}>No updates yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, tone }) {
  return (
    <div style={{ background: T.panel, borderRadius: 10, padding: "14px 16px", flex: 1, minWidth: 200 }}>
      <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: fontDisplay,
          fontSize: 30,
          marginTop: 4,
          color: tone === "bad" ? T.cost : T.ink,
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/**
 * A window of the forecast, at both ends.
 *
 * The trough on its own says how bad it gets; the peak beside it says whether
 * that is a dip or a direction. Two windows are shown because they answer
 * different questions — thirty days is "is there a problem this month", a
 * quarter is "is there a problem coming".
 */
function RangeKPI({ label, low, high, overdraft }) {
  const past = overdraft > 0 && low && low.endBal < -overdraft;
  const col = { flex: 1, minWidth: 0 };
  const cap = {
    fontSize: 9,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    color: T.muted,
    marginBottom: 2,
  };
  const fig = { fontFamily: fontDisplay, fontSize: 23, lineHeight: 1.15 };
  const when = { fontSize: 10, color: T.muted, marginTop: 2 };
  return (
    <div
      style={{
        background: T.panel,
        borderRadius: 10,
        padding: "14px 16px",
        flex: 1,
        minWidth: 260,
        border: past ? `1px solid ${T.cost}` : undefined,
      }}
    >
      <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
        {label}
      </div>
      {/* Both ends, given equal weight. The trough alone says how bad it gets
          without saying whether it is a dip or a direction, and burying the
          peak in small print underneath made it easy to miss the difference. */}
      <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
        <div style={col}>
          <div style={cap}>Lowest</div>
          <div style={{ ...fig, color: low && low.endBal < 0 ? T.cost : T.ink }}>
            {low ? fmtMoney(low.endBal) : "—"}
          </div>
          <div style={when}>{low ? `w/e ${fmtDay(isoDate(low.end))}` : ""}</div>
        </div>
        <div style={{ width: 1, background: T.borderSoft }} />
        <div style={col}>
          <div style={cap}>Highest</div>
          <div style={{ ...fig, color: high && high.endBal < 0 ? T.cost : T.ink }}>
            {high ? fmtMoney(high.endBal) : "—"}
          </div>
          <div style={when}>{high ? `w/e ${fmtDay(isoDate(high.end))}` : ""}</div>
        </div>
      </div>
      {past && (
        <div style={{ fontSize: 10, color: T.cost, fontWeight: 700, marginTop: 8 }}>
          past the overdraft
        </div>
      )}
    </div>
  );
}

/**
 * What the colours and marks mean.
 *
 * The grid encodes a lot in very little space — a tint, a tick, a small orange
 * number — and none of it is guessable. Anyone reading a cash forecast for the
 * first time deserves to be told rather than left to infer.
 */
/**
 * Which report to take away.
 *
 * A single export button had to mean one thing, and the one thing it meant was
 * the cash flow. Everything else worth exporting — who owes what, how late —
 * had no home, so it lives behind the same button as a short list rather than
 * as three more circles stacked up the side of the screen.
 */
function ExportMenu({ busy, counts, onPick, onClose }) {
  const ref = useRef(null);

  // Click anywhere else, or press escape, and it goes away. A menu pinned to a
  // floating button is easy to open by accident and must be easy to dismiss.
  useEffect(() => {
    const away = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const key = (e) => e.key === "Escape" && onClose();
    // Deferred, or the click that opened it closes it again.
    const t = setTimeout(() => document.addEventListener("mousedown", away), 0);
    document.addEventListener("keydown", key);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);

  const options = [
    {
      kind: "cashflow",
      title: "Cash flow",
      note: "The weekly grid, and every line behind it",
    },
    {
      kind: "income",
      title: "Outstanding invoices",
      note: counts?.income?.n
        ? `${counts.income.n} owed to us · ${fmtMoney(counts.income.sum)}${
            counts.income.late ? ` · ${counts.income.late} overdue` : ""
          }`
        : "Nothing outstanding",
      empty: !counts?.income?.n,
    },
    {
      kind: "suppliers",
      title: "Outstanding bills",
      note: counts?.suppliers?.n
        ? `${counts.suppliers.n} we owe · ${fmtMoney(counts.suppliers.sum)}${
            counts.suppliers.late ? ` · ${counts.suppliers.late} overdue` : ""
          }`
        : "Nothing outstanding",
      empty: !counts?.suppliers?.n,
    },
  ];

  return (
    <div
      ref={ref}
      role="menu"
      style={{
        position: "absolute",
        right: 0,
        bottom: 110,
        width: 268,
        background: T.panel,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        boxShadow: "0 8px 28px rgba(0,0,0,0.16)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "9px 13px",
          fontSize: 10,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: T.muted,
          background: T.panelSoft,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        Export to Excel
      </div>
      {options.map((o) => (
        <button
          key={o.kind}
          role="menuitem"
          onClick={() => !o.empty && onPick(o.kind)}
          disabled={!!busy || o.empty}
          style={{
            display: "block",
            width: "100%",
            textAlign: "left",
            padding: "11px 13px",
            border: "none",
            borderTop: `1px solid ${T.borderSoft || T.border}`,
            background: "transparent",
            cursor: busy || o.empty ? "default" : "pointer",
            fontFamily: "inherit",
            opacity: o.empty ? 0.45 : 1,
          }}
          onMouseEnter={(e) => {
            if (!busy && !o.empty) e.currentTarget.style.background = T.panelSoft;
          }}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <div style={{ fontSize: 13, color: T.ink }}>
            {o.title}
            {busy === o.kind && <span style={{ color: T.muted }}> · building…</span>}
          </div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{o.note}</div>
        </button>
      ))}
    </div>
  );
}

export function Legend() {
  const chip = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    color: T.muted,
    whiteSpace: "nowrap",
  };
  const swatch = (extra) => ({
    width: 13,
    height: 13,
    borderRadius: 3,
    display: "inline-block",
    flex: "0 0 auto",
    ...extra,
  });
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        alignItems: "center",
        background: T.panel,
        borderRadius: 10,
        padding: "10px 14px",
        marginBottom: 12,
      }}
    >
      <span style={{ ...chip, color: T.ink, fontWeight: 600 }}>Key</span>
      <span style={chip}>
        <span style={swatch({ background: T.income })} /> money in
      </span>
      <span style={chip}>
        <span style={swatch({ background: T.cost })} /> money out
      </span>
      <span style={chip}>
        <span style={{ ...swatch({ background: "#E7F0E7", border: `1px solid ${T.income}` }) }} />
        already in the bank
      </span>
      <span style={chip}>
        <span style={{ color: T.income, fontFamily: fontMono }}>✓</span> received or paid
      </span>
      <span style={chip}>
        <span
          style={{
            background: T.warn,
            color: "#fff",
            borderRadius: 3,
            fontSize: 9,
            fontWeight: 700,
            padding: "1px 4px",
            fontFamily: fontMono,
          }}
        >
          37d
        </span>
        days overdue · income only
      </span>
      <span style={chip}>
        <span style={{ color: T.pipeline, fontFamily: fontMono }}>◇</span> quote, not yet won
      </span>
      <span style={chip}>
        <span style={{ color: T.income, fontFamily: fontMono }}>⬦</span> agreed · waiting on a PO
      </span>
      <span style={chip}>
        <span style={{ color: T.muted, fontFamily: fontMono }}>⇄</span> has costs attached
      </span>
      <span style={chip}>
        <span style={swatch({ background: "#FBEFE2" })} /> below zero — into the overdraft
      </span>
      <span style={chip}>
        <span style={swatch({ background: "#F0C9C9" })} /> past the overdraft — the bank says no
      </span>
      <span style={chip}>
        <span style={{ color: T.muted, fontSize: 9, fontFamily: fontMono }}>invoiced / billed</span>{" "}
        on a total — the part a document obliges, the rest is expected
      </span>
    </div>
  );
}

/**
 * One forecast, built one way.
 *
 * The investor link shows the same grid as the team, so it has to be built by
 * the same code — a second copy of this wiring is how the two views start
 * quietly disagreeing about the same week.
 */
export function buildForecast(d, openingDate, historyWeeks = 1) {
  if (!d) return null;
  return buildTransactions({
    historyWeeks,
    scoroInvoices: d.scoroInvoices,
    // Scoro's bills stand down the moment Xero has any, rather than on a date
    // somebody has to remember. Xero holds the same bills at the same amounts
    // and a great many more besides, so running both would double every one of
    // them — but dropping Scoro before Xero arrives would leave the forecast
    // with no expenditure at all, which is worse. The handover happens by
    // itself, in one direction, with no window where money goes missing.
    scoroBills: d.xeroBills?.length ? [] : d.scoroBills,
    xeroBills: d.xeroBills,
    xeroPaidBills: d.xeroPaidBills,
    expectedDates: d.expectedDates,
    docSpend: d.docSpend,
    recurring: d.recurring,
    amexSettings: d.amexSettings,
    amexCharges: d.amexCharges,
    quotes: d.quotes,
    quoteStatuses: d.quoteStatuses,
    quoteRules: d.quoteRules,
    quoteOverrides: d.quoteOverrides,
    onAccount: d.onAccount,
    paidInvoices: d.paidInvoices,
    manualEntries: d.manualEntries,
    instalments: d.instalments,
    amexPayments: d.amexPayments,
    paydownOverrides: d.paydownOverrides,
    recurringOverrides: d.recurringOverrides,
    paidMarks: d.paidMarks,
    dismissals: d.dismissals,
    retainers: d.retainers,
    retainerSettings: d.retainerSettings,
    fxRates: d.appSettings?.fx_rates || {},
    openingDateIso: openingDate,
    projectionWeeks: 26,
  });
}

export function bucketWeeks(forecast, openingBalance, openingDate, historyWeeks = 1) {
  if (!forecast) return [];
  return bucketByWeek(forecast.txns, {
    anchor: forecast.anchor,
    historyWeeks,
    projectionWeeks: 26,
    openingBalance: Number(openingBalance) || 0,
    openingDateIso: openingDate,
  });
}

export function headlineFigures(d, forecast) {
  /**
   * Foreign invoices count, converted.
   *
   * They used to be left out of "owed to us" entirely — the right answer when
   * there was no exchange rate to convert them with, since a dollar added to a
   * pound is a wrong number. Once a rate was set they should have joined in,
   * and did everywhere except here: the forecast carried Spyscape at £14,114
   * while the headline said £315,707 against Scoro's £329,821.
   *
   * Still left out if no rate exists for the currency, because that has not
   * stopped being true — but silently, rather than pretending the money is not
   * owed.
   */
  const rates = d?.appSettings?.fx_rates || {};
  const rateFor = (ccy) => (!ccy || ccy === "GBP" ? 1 : rates[ccy] ?? null);
  const owedRows = (d?.scoroInvoices || [])
    .map((r) => ({ ...r, fx: rateFor(r.currency) }))
    .filter((r) => r.fx != null);
  const value = (r) => Number(r.outstanding || 0) * r.fx;
  const late = (r) => r.deadline && r.deadline < todayStr();
  return {
    owed: owedRows.reduce((s, r) => s + value(r), 0),
    owedOverdue: owedRows.filter(late).reduce((s, r) => s + value(r), 0),
    // What is owed but cannot be counted, so the headline can say it is
    // missing rather than quietly understating.
    owedUnconverted: (d?.scoroInvoices || [])
      .filter((r) => rateFor(r.currency) == null)
      .reduce((s, r) => s + Number(r.outstanding || 0), 0),
    amexHeadroom: forecast?.amex?.headroom ?? null,
  };
}

/**
 * Group the forecast into the rows the grid draws.
 *
 * Exported because the spreadsheet export has to produce the same rows in the
 * same order — a download that disagrees with the screen is worse than no
 * download, and it would disagree the first time either changed.
 */
export function buildGridRows(weeks, spendByDoc = {}) {
    // Income keeps a row per client — that is who owes you, and the names matter.
    // Costs collapse into categories; the suppliers behind each are one click away.
    const income = {};
    const costs = {};
    weeks.forEach((w, wi) => {
      for (const t of w.txns) {
        const isPipe = !!t.isPipeline;
        const bucket = t.amount >= 0 ? income : costs;
        // One row per client, invoiced and quoted together — a client is a
        // client. The distinction is carried in the cell instead, so a week
        // holding both still says so rather than splitting the client in two.
        // Costs group by category, except manual entries, which keep the name
        // they were given: someone typing "FlexiPay" wants a FlexiPay row, not
        // to have it folded into Debt alongside everything else.
        const key =
          t.amount >= 0 ? t.label : t.isManual ? `manual:${t.label}` : t.category || "other";
        bucket[key] = bucket[key] || {
          key,
          label: t.label,
          manual: !!t.isManual,
          lending: !!t.isLending,
          hasPipeline: false,
          // Documents on this row that carry attached spend, so the row can
          // offer the link and light up the costs it pays for.
          spendKeys: new Set(),
          cells: Array(weeks.length).fill(null),
        };
        if (isPipe) bucket[key].hasPipeline = true;
        const dk = t.source?.type ? `${t.source.type}:${t.source.id}` : null;
        if (dk && spendByDoc[dk]?.length) bucket[key].spendKeys.add(dk);
        const cell = bucket[key].cells[wi] || {
          total: 0,
          invoiced: 0,
          quoted: 0,
          awaitingPo: 0,
          received: 0,
          paid: 0,
          history: 0,
          committed: 0,
          docKeys: new Set(),
          linkedTo: new Set(),
          // How much of this cell belongs to each document it is linked to. A
          // card repayment settles several jobs at once, so "which documents"
          // is not enough to answer "how much of this is theirs".
          linkedAmounts: {},
          txns: [],
          overdue: 0,
          worstLate: 0,
        };
        cell.total += t.amount;
        // "Confirmed" splits by direction: money in that has landed, and money
        // out that has already gone. They mean the same thing about certainty
        // and opposite things about the balance, so they are counted apart.
        if (t.isHistory) cell.history += t.amount;
        if (t.isReceived && t.amount >= 0) cell.received += t.amount;
        else if (t.isReceived) cell.paid += t.amount;
        else if (isPipe) {
          cell.quoted += t.amount;
          if (t.isAwaitingPo) cell.awaitingPo += t.amount;
        }
        else cell.invoiced += t.amount;
        cell.txns.push(t);
        if (t.committed && !t.isHistory) cell.committed += t.amount;
        // An income cell knows which documents it holds; an attached-cost cell
        // knows which document it belongs to. That pair is the whole link.
        if (t.source?.type && t.amount >= 0) cell.docKeys.add(`${t.source.type}:${t.source.id}`);
        if (t.source?.attachedTo) {
          const k = `${t.source.attachedTo.source}:${t.source.attachedTo.id}`;
          cell.linkedTo.add(k);
          cell.linkedAmounts[k] = (cell.linkedAmounts[k] || 0) + t.amount;
        }
        // An AMEX repayment is linked to the documents whose charges that
        // statement is actually settling — not to every piece of income that
        // has ever touched the card.
        for (const k of t.docKeys || t.source?.docKeys || []) cell.linkedTo.add(k);
        const shares = t.docAmounts || t.source?.docAmounts;
        if (shares) {
          for (const [k, v] of Object.entries(shares)) {
            cell.linkedAmounts[k] = (cell.linkedAmounts[k] || 0) - v;
          }
        }
        // Overdue is a property of the DOCUMENT's due date, not of the week it
        // has been moved to — an invoice pushed to November is still 94 days
        // late, and that is exactly what needs to stay visible.
        const late = t.source?.daysLate || 0;
        if (late > 0) {
          cell.overdue++;
          cell.worstLate = Math.max(cell.worstLate, late);
        }
        bucket[key].cells[wi] = cell;
      }
    });
    const all = Object.values(income).sort((a, b) => a.label.localeCompare(b.label));
    return {
      // Lending sits under the clients rather than among them: it is money in,
      // but it is borrowed, not earned, and a row of it filed alphabetically
      // between two clients reads like trading.
      incomeRows: all.filter((r) => !r.lending),
      lendingRows: all.filter((r) => r.lending),
      // Fixed category order, so the shape of the costs is the same every week.
      costRows: [
        ...COST_CATEGORIES.filter((c) => costs[c.id]).map((c) => ({ ...costs[c.id], label: c.label })),
        ...Object.values(costs)
          .filter((r) => r.manual)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ],
      histCount: weeks.filter((w) => w.isHistory).length,
    };
}

export function CashFlowGrid({ weeks, onPick, spendByDoc = {}, overdraftLimit = 0, openingDate, bankByWeekEnd = {} }) {
  // Which income is being pointed at, so the cost it pays for can light up.
  const [linked, setLinked] = useState(null);
  const [linkedLabel, setLinkedLabel] = useState("");
  const { incomeRows, lendingRows, costRows, histCount } = useMemo(
    () => buildGridRows(weeks, spendByDoc),
    [weeks, spendByDoc]
  );

  const num = {
    padding: "6px 10px",
    textAlign: "right",
    fontFamily: fontMono,
    fontSize: 11,
    whiteSpace: "nowrap",
  };
  const nameCell = {
    padding: "6px 10px",
    fontSize: 12,
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    background: T.panel,
    zIndex: 1,
  };

  // Totals must agree with the balance walk, which ignores history. Showing a
  // history figure on its own row is context; letting it into the total makes
  // the column stop adding up, which is worse than not showing it at all.
  // Settled money is normally left out of a column total, because it is already
  // inside the opening balance and letting it in stops the column reconciling
  // with the balance walk. A week in the past has no balance walk to reconcile
  // with — what moved is the whole point of looking — so there it counts.
  //
  // A week that had already begun when the balance was struck counts its
  // settled money too. That is every past week, and — once the opening date is
  // anchored to the bank each night — the current one as well, which by Friday
  // has all of itself behind the balance. The walk takes that money back off
  // before it starts, so the column reconciles either way.
  const settledCounts = (wi) => {
    const w = weeks[wi];
    if (!w) return false;
    return !!w.isHistory || (!!openingDate && isoDate(w.start) <= openingDate);
  };
  const total = (rows, wi) =>
    rows.reduce((sum, r) => {
      const c = r.cells[wi];
      if (!c) return sum;
      return sum + (settledCounts(wi) ? c.total : c.total - (c.history || 0));
    }, 0);
  // The share of a column that somebody is actually obliged by.
  const committed = (rows, wi) => rows.reduce((sum, r) => sum + (r.cells[wi]?.committed || 0), 0);
  /** Money that moved in this week but is already inside the opening balance. */
  const banked = (rows, wi) =>
    weeks[wi]?.isHistory ? 0 : rows.reduce((sum, r) => sum + (r.cells[wi]?.history || 0), 0);
  /** Everything a week holds, settled or not — what the drawer opens on. */
  const allTxns = (rows, wi) => rows.flatMap((r) => r.cells[wi]?.txns || []);

  // A cell lights up if it holds the hovered income, or the cost that income
  // pays for. Nothing else changes, so the link reads as a relationship rather
  // than a filter.
  const isLit = (cell) => {
    if (!linked || !cell) return false;
    for (const k of linked) {
      if (cell.docKeys.has(k) || cell.linkedTo.has(k)) return true;
    }
    return false;
  };

  /**
   * The part of a lit cell that belongs to what is being hovered.
   *
   * A supplier bill raised for one job is the whole cell, so nothing changes.
   * A card repayment is clearing several jobs at once, and showing £48,400
   * against one client implies the whole statement is theirs.
   */
  const litShare = (cell) => {
    if (!linked || !cell) return null;
    let share = 0;
    let known = false;
    for (const k of linked) {
      if (cell.linkedAmounts[k] != null) {
        share += cell.linkedAmounts[k];
        known = true;
      }
    }
    if (!known) return null;
    // Cost rows hand the cell an absolute total, so the share has to follow the
    // same convention or a cell reading £22,742 sprouts a minus sign the moment
    // it is pointed at.
    const whole = Math.abs(cell.total);
    const size = Math.abs(share);
    // No point replacing a figure with itself: a bill raised for one job is the
    // whole cell, and only the outline needs to say so.
    if (Math.abs(size - whole) < 1) return null;
    return cell.total < 0 ? -size : size;
  };

  // Where the linked money actually is.
  //
  // The outline only helps if the cell is on screen, and a grid twenty-six
  // weeks wide guarantees it often is not. This says which rows, which weeks
  // and how much, without anybody having to go looking.
  const linkSummary = useMemo(() => {
    if (!linked) return null;
    const out = [];
    for (const r of costRows) {
      r.cells.forEach((c, i) => {
        if (!c) return;
        let share = 0;
        for (const k of linked) if (c.linkedAmounts[k] != null) share += c.linkedAmounts[k];
        if (Math.abs(share) < 1) return;
        out.push({ row: r.label, week: weeks[i]?.start, amount: Math.abs(share) });
      });
    }
    return out.sort((a, b) => a.week - b.week);
  }, [linked, costRows, weeks]);

  const Cell = ({ cell, colour, onClickCell, income }) => {
    if (!cell) return <td style={num}><span style={{ color: T.border }}>–</span></td>;
    const lit = isLit(cell);
    const share = lit ? litShare(cell) : null;
    // Pure quote money reads violet; a week holding both keeps the invoiced
    // colour and carries a ◇ so the quoted part is not disguised as billed work.
    // Work that is agreed and only waiting on a PO number is not speculation,
    // so it is not tinted or marked as though it were.
    const pureQuote = cell.quoted && !cell.awaitingPo && !cell.invoiced && !cell.received;
    const onlyAwaitingPo = cell.quoted && cell.quoted === cell.awaitingPo;
    const mixed = cell.quoted && !onlyAwaitingPo && (cell.invoiced || cell.received);
    const allReceived = cell.received && !cell.invoiced && !cell.quoted;
    const allPaid = cell.paid && !cell.invoiced && !cell.quoted && !cell.received;
    // A tick on a figure that is only partly settled claims more than is true.
    // When a week holds both, the figure stays neutral and the settled share is
    // spelled out underneath instead.
    const settled = (cell.received || 0) + (cell.paid || 0);
    const partlySettled = settled !== 0 && !allReceived && !allPaid;
    const tip = [
      cell.history
        ? "already inside the opening balance — shown for context, not counted"
        : null,
      cell.received ? `${fmtMoney(Math.abs(cell.received))} RECEIVED` : null,
      cell.paid ? `${fmtMoney(Math.abs(cell.paid))} ALREADY PAID` : null,
      cell.invoiced ? `${fmtMoney(Math.abs(cell.invoiced))} invoiced` : null,
      cell.awaitingPo ? `${fmtMoney(Math.abs(cell.awaitingPo))} agreed, awaiting a PO` : null,
      cell.quoted - cell.awaitingPo
        ? `${fmtMoney(Math.abs(cell.quoted - cell.awaitingPo))} quoted, not yet won`
        : null,
      income && cell.overdue ? `${cell.overdue} overdue · oldest ${cell.worstLate} days past due` : null,
      share != null ? `${fmtMoney(share)} of ${fmtMoney(cell.total)} relates to what you are pointing at` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <td
        style={{
          ...num,
          outline: lit ? `2px solid ${T.pipeline}` : undefined,
          outlineOffset: -2,
          // Confirmed money gets a filled ground, so a glance down the row shows
          // what has actually landed without reading any figures.
          background: partlySettled
            ? undefined
            : cell.received
            ? T.receivedSoft
            : cell.paid
              ? T.settledSoft
              // Overdue money needs to be findable by scanning, not by reading
              // every figure for a small mark.
              : income && cell.overdue
                ? T.overdueSoft
                : undefined,
        }}
      >
        <button
          onClick={onClickCell}
          title={tip || "Show the documents behind this figure"}
          style={{
            border: "none",
            background: "none",
            font: "inherit",
            color: cell.history
              ? T.muted
              : allReceived
                ? T.received
                : allPaid
                  ? T.settled
                  : pureQuote
                    ? T.pipeline
                    : colour,
            fontStyle: cell.history ? "italic" : "normal",
            fontWeight: allReceived || allPaid ? 600 : 400,
            cursor: "pointer",
            padding: 0,
          }}
        >
          {cell.history ? (
            <span style={{ color: T.muted, marginRight: 3, fontSize: 9 }} title="already in the opening balance">
              ·
            </span>
          ) : null}
          {cell.received && !partlySettled ? (
            <span style={{ color: T.received, marginRight: 3 }}>✓</span>
          ) : null}
          {cell.paid && !partlySettled ? (
            <span style={{ color: T.settled, marginRight: 3 }}>✓</span>
          ) : null}
          {income && cell.overdue ? (
            <span
              style={{
                background: T.overdue,
                color: "#fff",
                borderRadius: 3,
                padding: "0 4px",
                marginRight: 4,
                fontSize: 9,
                fontWeight: 700,
                verticalAlign: 1,
              }}
            >
              {cell.worstLate}d
            </span>
          ) : null}
          {cell.awaitingPo ? (
            <span style={{ color: T.income, marginRight: 2 }} title="agreed — waiting on a PO">
              ⬦
            </span>
          ) : null}
          {mixed ? <span style={{ color: T.pipeline, marginRight: 2 }}>◇</span> : null}
          {share != null ? (
            // Showing the whole statement against one client implies all of it
            // is theirs. The share is what answers the question being asked.
            <span style={{ color: T.pipeline, fontWeight: 700 }}>{fmtMoney(share)}</span>
          ) : (
            fmtMoney(cell.total)
          )}
          {share != null && (
            <div style={{ fontSize: 9, color: T.pipeline, fontWeight: 400 }}>
              of {fmtMoney(cell.total)}
            </div>
          )}
          {partlySettled && (
            <div style={{ fontSize: 9, color: cell.received ? T.received : T.settled, fontWeight: 400 }}>
              ✓ {fmtMoney(Math.abs(settled))} {cell.received ? "in" : "paid"}
            </div>
          )}
        </button>
      </td>
    );
  };

  const SectionHeader = ({ title }) => (
    <tr>
      <td
        colSpan={weeks.length + 1}
        style={{
          padding: "5px 10px",
          fontStyle: "italic",
          fontSize: 12,
          background: "#DCE6DD",
          position: "sticky",
          left: 0,
        }}
      >
        {title}
      </td>
    </tr>
  );

  return (
    <div style={{ overflowX: "auto", background: T.panel, borderRadius: 10, border: `1px solid ${T.borderSoft}` }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            <th style={{ ...nameCell, textAlign: "left", fontSize: 12, padding: "10px" }}>WEEK</th>
            {weeks.map((w, i) => {
              const isCurrent = !w.isHistory && i === histCount;
              const { week: fyWk } = fyWeek(w.start);
              const weeksAgo = i - histCount;
              const sub = w.isHistory
                ? weeksAgo === -1
                  ? "LAST WEEK"
                  : `${-weeksAgo}W AGO`
                : isCurrent
                  ? `THIS WEEK · W${fyWk}`
                  : `W${fyWk}`;
              return (
                <th
                  key={i}
                  style={{
                    padding: "8px 10px",
                    textAlign: "right",
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    background: w.isHistory ? T.borderSoft : "transparent",
                    color: w.isHistory ? T.muted : T.ink,
                  }}
                >
                  <div>{fmtWeek(w.start)}</div>
                  <div style={{ fontWeight: 400, fontSize: 9, color: T.muted, marginTop: 2 }}>{sub}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <SectionHeader title="Income" />
          {incomeRows.map((r) => (
            <tr
              key={`in-${r.key}`}
              onPointerEnter={(e) => {
                // A finger must not trigger this. iPadOS spends the first tap
                // producing the hover and withholds the click, so a row that
                // lights up its linked costs on hover is a row that needs
                // tapping twice — which reads as the tap not working at all.
                if (e.pointerType !== "mouse") return;
                if (!r.spendKeys.size) return;
                setLinked(new Set(r.spendKeys));
                setLinkedLabel(r.label);
              }}
              onPointerLeave={(e) => {
                if (e.pointerType !== "mouse") return;
                setLinked(null);
              }}
              style={{ borderTop: `1px solid ${T.borderSoft}` }}
            >
              <td style={nameCell}>
                <button
                  onClick={() => onPick(r.cells.flatMap((c) => c?.txns || []), r.label)}
                  style={{ border: "none", background: "none", font: "inherit", cursor: "pointer", padding: 0, textAlign: "left" }}
                >
                  <span style={{ color: T.income, marginRight: 6 }}>●</span>
                  <span>{r.label}</span>
                  {r.spendKeys.size > 0 && (
                    <span
                      style={{ fontSize: 10, color: T.pipeline, marginLeft: 5 }}
                      title="Has spend attached — hover to see which costs it pays for"
                    >
                      ⇄
                    </span>
                  )}
                  {r.hasPipeline && (
                    <span
                      style={{ fontSize: 10, color: T.pipeline, marginLeft: 5 }}
                      title="Some of this client's income is quoted, not invoiced"
                    >
                      ◇
                    </span>
                  )}
                </button>
              </td>
              {r.cells.map((c, i) => (
                <Cell
                  key={i}
                  cell={c}
                  income
                  colour={T.income}
                  onClickCell={() => onPick(c.txns, `${r.label} · ${fmtWeek(weeks[i].start)}`)}
                />
              ))}
            </tr>
          ))}
          {lendingRows.length > 0 && (
            <tr>
              <td
                style={{
                  ...nameCell,
                  fontStyle: "italic",
                  fontSize: 11,
                  color: T.muted,
                  background: "#E7F0E7",
                  borderTop: `1px solid ${T.income}`,
                }}
              >
                Lending &amp; investment
              </td>
              <td
                colSpan={weeks.length}
                style={{ background: "#E7F0E7", borderTop: `1px solid ${T.income}` }}
              />
            </tr>
          )}
          {lendingRows.map((r) => (
            <tr key={`lend-${r.key}`} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
              <td style={nameCell}>
                <button
                  onClick={() => onPick(r.cells.flatMap((c) => c?.txns || []), r.label)}
                  style={{ border: "none", background: "none", font: "inherit", cursor: "pointer", padding: 0, textAlign: "left" }}
                >
                  <span style={{ color: T.income, marginRight: 6 }}>◈</span>
                  {r.label}
                </button>
              </td>
              {r.cells.map((c, i) => (
                <Cell
                  key={i}
                  cell={c}
                  colour={T.income}
                  income
                  onClickCell={() => onPick(c.txns, `${r.label} · ${fmtWeek(weeks[i].start)}`)}
                />
              ))}
            </tr>
          ))}

          <tr style={{ background: "#EDF3ED", borderTop: `1px solid ${T.border}` }}>
            <td style={{ ...nameCell, fontWeight: 600, color: T.income, background: "#EDF3ED" }}>Total income</td>
            {weeks.map((w, i) => {
              const withLending = [...incomeRows, ...lendingRows];
              const already = banked(withLending, i);
              const toCome = total(withLending, i) - already;
              // Everything that moves this week, not just what is left to come.
              //
              // The total used to show only the part still outstanding, because
              // money already inside the opening balance must stay out of the
              // balance walk or the column stops adding up. That was invisible
              // while the opening balance was struck months ago. Anchoring it
              // to the bank each night put most of a week's receipts on the
              // wrong side of it, and a week in which £79,000 arrived read as
              // £4,705.
              //
              // So the headline is what came in, and the part already banked is
              // named underneath.
              const all = toCome + already;
              const sure = committed(withLending, i);
              return (
                <td key={i} style={{ ...num, fontWeight: 600, color: T.income }}>
                  {all ? fmtMoney(all) : <span style={{ color: T.border }}>–</span>}
                  {already > 0 && (
                    <div
                      style={{ fontSize: 9, fontWeight: 400, color: T.received }}
                      title="Already in the bank, so already inside the opening balance — counted here, but not again in the balance below"
                    >
                      ✓ {fmtMoney(already)} in
                    </div>
                  )}
                  {!w.isHistory && toCome > 0 && Math.round(sure) !== Math.round(toCome) && (
                    <div
                      style={{ fontSize: 9, fontWeight: 400, color: T.muted }}
                      title="Invoiced — the rest is quoted or not yet billed"
                    >
                      {fmtMoney(sure)} invoiced
                    </div>
                  )}
                </td>
              );
            })}
          </tr>

          <SectionHeader title="Costs" />
          {costRows.map((r) => (
            <tr key={`out-${r.key}`} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
              {(() => {
                // The label column is sticky, so lighting it says "this row is
                // involved" even when the cell itself is far off to the right.
                const rowShare = linked
                  ? r.cells.reduce((sum, c) => {
                      if (!c) return sum;
                      let v = 0;
                      for (const k of linked) if (c.linkedAmounts[k] != null) v += c.linkedAmounts[k];
                      return sum + v;
                    }, 0)
                  : 0;
                const involved = Math.abs(rowShare) >= 1;
                return (
                  <td
                    style={{
                      ...nameCell,
                      background: involved ? "#F4F1FA" : nameCell.background,
                      boxShadow: involved ? `inset 3px 0 0 ${T.pipeline}` : undefined,
                    }}
                  >
                    <button
                      onClick={() => onPick(r.cells.flatMap((c) => c?.txns || []), r.label)}
                      title={`Every ${r.label.toLowerCase()} cost in the horizon`}
                      style={{ border: "none", background: "none", font: "inherit", cursor: "pointer", padding: 0, textAlign: "left" }}
                    >
                      <span style={{ color: T.cost, marginRight: 6 }}>●</span>
                      {r.label}
                      {involved && (
                        <span style={{ color: T.pipeline, fontWeight: 700, marginLeft: 6, fontFamily: fontMono, fontSize: 11 }}>
                          {fmtMoney(Math.abs(rowShare))}
                        </span>
                      )}
                    </button>
                  </td>
                );
              })()}
              {r.cells.map((c, i) => (
                <Cell
                  key={i}
                  cell={c ? { ...c, total: Math.abs(c.total) } : null}
                  colour={T.cost}
                  onClickCell={() => onPick(c.txns, `${r.label} · ${fmtWeek(weeks[i].start)}`)}
                />
              ))}
            </tr>
          ))}
          <tr style={{ background: "#F7E9E9", borderTop: `1px solid ${T.border}` }}>
            <td style={{ ...nameCell, fontWeight: 600, color: T.cost, background: "#F7E9E9" }}>Total costs</td>
            {weeks.map((w, i) => {
              const all = total(costRows, i);
              // Costs had no equivalent of the income row's banked line, so a
              // week whose payments had all gone out read as zero spent while
              // the rows above it showed the payments.
              const gone = banked(costRows, i);
              const sure = committed(costRows, i);
              return (
                <td key={i} style={{ ...num, fontWeight: 600, color: T.cost }}>
                  {all ? fmtMoney(Math.abs(all)) : <span style={{ color: T.border }}>–</span>}
                  {gone < 0 && (
                    <div
                      style={{ fontSize: 9, fontWeight: 400, color: T.received }}
                      title="Already left the account, so already inside the opening balance — counted here, but not again in the balance below"
                    >
                      ✓ {fmtMoney(Math.abs(gone))} paid
                    </div>
                  )}
                  {!w.isHistory && all !== 0 && Math.round(sure) !== Math.round(all) && (
                    <div
                      style={{ fontSize: 9, fontWeight: 400, color: T.muted }}
                      title="Already billed to us — the rest is expected"
                    >
                      {fmtMoney(Math.abs(sure))} billed
                    </div>
                  )}
                </td>
              );
            })}
          </tr>

          <tr style={{ borderTop: `1px solid ${T.border}` }}>
            <td style={nameCell}>Net change</td>
            {weeks.map((w, i) => {
              const net = w.in + w.out;
              return (
                <td key={i} style={{ ...num, color: net < 0 ? T.cost : T.income }}>
                  {net === 0 ? <span style={{ color: T.border }}>–</span> : `${net > 0 ? "+" : ""}${fmtMoney(net)}`}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={nameCell}>Opening balance</td>
            {weeks.map((w, i) => {
              // A week that finished before the balance was struck has a
              // measured opening too — last week's measured close. Preferred
              // over the derived walk for the same reason the closing row
              // prefers it: the bank knows, and the walk only infers.
              const before = openingDate && isoDate(w.end) < openingDate;
              const actual = before ? bankByWeekEnd[isoDate(addDays(w.start, -1))] : null;
              const value = actual ? actual.balance : w.startBal;
              return (
                <td
                  key={i}
                  title={actual ? `Actual bank balance, read on ${fmtDay(actual.on)}` : ""}
                  style={{
                    ...num,
                    color: value < 0 ? T.cost : T.ink,
                    background: actual ? T.panelSoft : undefined,
                  }}
                >
                  {fmtMoney(value)}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={{ ...nameCell, fontWeight: 600 }}>Closing balance</td>
            {weeks.map((w, i) => {
              // Only weeks that finished before the opening balance was struck
              // have no balance worth showing — that figure is where the
              // balance starts, and everything earlier is already inside it.
              //
              // This used to blank every past week, which was the same thing
              // when history meant "before the opening balance". It stopped
              // being the same thing the moment the opening date moved back and
              // the look-back was added: last week now sits well after it, has
              // a real closing balance, and was showing a dash.
              const beforeOpening = openingDate && isoDate(w.end) < openingDate;

              // A week that finished before the balance was struck has no
              // forecast worth showing — but the bank knows what happened, and
              // a real figure beats both a dash and a derived one. Anchoring to
              // the account each night turned every past week into a dash;
              // this puts the history back, measured rather than inferred.
              const actual = beforeOpening ? bankByWeekEnd[isoDate(w.end)] : null;
              if (actual) {
                return (
                  <td
                    key={i}
                    title={`Actual bank balance, read on ${fmtDay(actual.on)}`}
                    style={{
                      ...num,
                      background: T.panelSoft,
                      color: actual.balance < 0 ? T.cost : T.ink,
                      fontWeight: 600,
                    }}
                  >
                    {fmtMoney(actual.balance)}
                    <div style={{ fontSize: 9, fontWeight: 400, color: T.muted }}>actual</div>
                  </td>
                );
              }

              if (beforeOpening) {
                return (
                  <td key={i} style={{ ...num, color: T.border, background: T.panelSoft }}>
                    –
                  </td>
                );
              }
              // Below zero is the overdraft doing its job. Below the overdraft
              // is the bank refusing the payment, which is a different kind of
              // problem and should not look the same.
              const past = overdraftLimit > 0 && w.endBal < -overdraftLimit;
              return (
                <td
                  key={i}
                  title={
                    past
                      ? `${fmtMoney(-(w.endBal + overdraftLimit))} beyond a ${fmtMoney(overdraftLimit)} overdraft`
                      : ""
                  }
                  style={{
                    ...num,
                    fontWeight: past ? 800 : 600,
                    color: w.endBal < 0 ? T.cost : T.ink,
                    background: past ? "#F0C9C9" : w.endBal < 0 ? "#F7E9E9" : "#EAF0EA",
                  }}
                >
                  {fmtMoney(w.endBal)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>

      {/* Pinned to the corner, because the answer is no use where the money
          happens to be — twenty-six weeks wide means it is usually off
          screen. */}
      {linkSummary?.length > 0 && (
        <div
          style={{
            position: "fixed",
            left: 22,
            bottom: 22,
            zIndex: 45,
            background: "#F4F1FA",
            border: `1px solid ${T.pipeline}`,
            borderRadius: 10,
            padding: "12px 14px",
            boxShadow: "0 4px 18px rgba(0,0,0,0.12)",
            maxWidth: 320,
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: 11, color: T.pipeline, fontWeight: 700, marginBottom: 6 }}>
            {linkedLabel} pays for
          </div>
          {linkSummary.slice(0, 8).map((x, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 10,
                fontSize: 11,
                padding: "2px 0",
                justifyContent: "space-between",
              }}
            >
              <span style={{ color: T.ink }}>
                {x.row}
                <span style={{ color: T.muted }}> · {fmtWeek(x.week)}</span>
              </span>
              <span style={{ fontFamily: fontMono, color: T.cost }}>{fmtMoney(x.amount)}</span>
            </div>
          ))}
          {linkSummary.length > 8 && (
            <div style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>
              and {linkSummary.length - 8} more
            </div>
          )}
          <div
            style={{
              borderTop: `1px solid ${T.pipeline}`,
              marginTop: 6,
              paddingTop: 6,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            <span>Total cost</span>
            <span style={{ fontFamily: fontMono, color: T.cost }}>
              {fmtMoney(linkSummary.reduce((t, x) => t + x.amount, 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Several documents at once.
 *
 * Chasing a client usually produces one answer about all of their invoices —
 * "not this month" covers thirteen of them — so moving them one at a time is
 * thirteen chances to mis-key a date. Relative moves are offered alongside an
 * absolute date because "push it a fortnight" is the more common instinct, and
 * it keeps the existing spread of dates intact rather than collapsing thirteen
 * invoices onto one day.
 */
/**
 * Documents whose expected date can be moved.
 *
 * Written when every document came from Scoro, and left behind when bills moved
 * to Xero: a `startsWith("scoro_")` test quietly made every Xero bill
 * unselectable, so a week full of them could not be rescheduled at all. Naming
 * the types is longer and cannot rot the same way.
 */
const MOVABLE_TYPES = new Set(["scoro_invoice", "scoro_bill", "scoro_quote", "xero_bill"]);

/** Can this line be said to have gone out, or come in, on its date? */
const canBePaid = (t) => {
  const type = t.source?.type;
  if (t.isReceived || t.isHistory) return false;
  return (
    type === "scoro_invoice" ||
    type === "scoro_bill" ||
    type === "xero_bill" ||
    type === "manual_entry" ||
    (type === "recurring" && !!t.source?.occurrenceDate)
  );
};

function BulkPanel({ items, onClose, onOpenOne, onMoved, readOnly = false }) {
  const movable = items.filter((t) => t.source?.expectedDate && MOVABLE_TYPES.has(t.source?.type));
  // A week being reconciled is mostly standing costs, which cannot be moved
  // but can certainly be ticked off, so selection is no longer tied to whether
  // a line happens to be a Scoro document.
  const payable = items.filter(canBePaid);

  // A standing cost can be taken out whether or not its date has passed.
  //
  // Once the opening balance moved to the bank, this week's overheads fell
  // inside it and stopped being selectable — but they are still on screen, and
  // an estimate that a real invoice already covered still reads as a payment
  // that happened. The reason for removing it does not expire just because the
  // date did.
  const removable = items.filter(
    (t) => t.source?.type === "recurring" && t.source?.occurrenceDate
  );
  const selectable = items.filter(
    (t) => movable.includes(t) || payable.includes(t) || removable.includes(t)
  );
  const [sel, setSel] = useState(() => Object.fromEntries(selectable.map((t) => [t.key, true])));
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const chosen = movable.filter((t) => sel[t.key]);
  const chosenPayable = payable.filter((t) => sel[t.key]);
  const chosenAll = selectable.filter((t) => sel[t.key]);
  const total = chosenAll.reduce((s, t) => s + t.amount, 0);
  const [paidDate, setPaidDate] = useState(todayStr());

  /**
   * Tick off everything selected in one go.
   *
   * Each kind records it in its own place — a document gets a paid mark, a
   * manual entry flips its received flag, a standing cost writes against that
   * occurrence — so this is a loop rather than one call, and it keeps going if
   * one of them fails so a single bad row cannot strand the rest.
   */
  const markPaid = async () => {
    if (!chosenPayable.length) return;
    setBusy(true);
    setError(null);
    const failed = [];
    for (const t of chosenPayable) {
      try {
        const src = t.source;
        if (src.type === "manual_entry") await updateManualEntry(src.id, { received: true });
        else if (src.type === "recurring") await setRecurringPaid(src.id, src.occurrenceDate, true);
        else await markDocPaid(src.type, src.id, paidDate);
      } catch (e) {
        failed.push(t.label);
      }
    }
    if (failed.length) {
      setError(`Could not mark ${failed.length}: ${failed.slice(0, 3).join(", ")}`);
      setBusy(false);
      return;
    }
    await onMoved();
  };

  const apply = async (targetDate) => {
    if (!chosen.length || !targetDate) return;
    setBusy(true);
    setError(null);
    try {
      await saveExpectedDates(
        chosen.map((t) => ({ type: t.source.type, id: t.source.id })),
        targetDate
      );
      await onMoved();
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  // Relative moves shift each document from its own expected date, so a spread
  // stays a spread.
  const applyRelative = async (days) => {
    if (!chosen.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const t of chosen) {
        const base = parseDate(t.source.expectedDate);
        if (!base) continue;
        await saveExpectedDate(t.source.type, t.source.id, isoDate(addDays(base, days)));
      }
      await onMoved();
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  const btn = {
    padding: "5px 10px",
    fontSize: 11,
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(26,24,21,0.35)", display: "flex", justifyContent: "flex-end", zIndex: 40, cursor: "pointer" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(520px,100%)", background: T.panel, padding: 20, overflowY: "auto", height: "100%", cursor: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div>
            <h2 style={{ fontFamily: fontDisplay, fontSize: 20, margin: 0 }}>
              {items.length} items in this week
            </h2>
            {(() => {
              const done = items.filter((t) => t.isReceived);
              const todo = items.filter((t) => !t.isReceived);
              const sum = (list) => list.reduce((s, t) => s + t.amount, 0);
              if (!done.length) return null;
              return (
                <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
                  <span style={{ color: T.settled, fontWeight: 600 }}>
                    ✓ {done.length} settled {fmtMoney(sum(done))}
                  </span>
                  {todo.length > 0 && (
                    <>
                      {" · "}
                      {todo.length} still to go {fmtMoney(sum(todo))}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: T.muted }}>
            ×
          </button>
        </div>

        {selectable.length > 0 && !readOnly && (
          <div style={{ background: T.panelSoft, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, margin: "12px 0" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={chosenAll.length === selectable.length}
                  onChange={(e) =>
                    setSel(Object.fromEntries(selectable.map((t) => [t.key, e.target.checked])))
                  }
                />
                {chosenAll.length} of {selectable.length} selected
              </label>
              <span style={{ fontFamily: fontMono, color: total >= 0 ? T.income : T.cost }}>
                {fmtMoney(total)}
              </span>
            </div>

            {chosenAll.some((t) => t.source?.type === "recurring") && !readOnly && (
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                style={{ ...btn, borderColor: T.warn, color: T.warn }}
                disabled={busy}
                onClick={async () => {
                  const rec = chosenAll.filter((t) => t.source?.type === "recurring");
                  setBusy(true);
                  setError(null);
                  try {
                    for (const t of rec) {
                      await setRecurringOverride(t.source.id, t.source.occurrenceDate, { skip: true });
                    }
                    await onMoved();
                  } catch (e) {
                    setError(e.message || String(e));
                    setBusy(false);
                  }
                }}
                title="Real invoices cover these — take the estimates out of this week"
              >
                {busy ? "Removing…" : `Bills cover ${chosenAll.filter((t) => t.source?.type === "recurring").length} of these`}
              </button>
            </div>
          )}

          {chosenPayable.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  style={{ ...btn, background: T.accent, color: "#fff", border: "none" }}
                  disabled={busy}
                  onClick={markPaid}
                >
                  {busy ? "Marking…" : `Mark ${chosenPayable.length} as paid`}
                </button>
                <span style={{ fontSize: 11, color: T.muted }}>on</span>
                <input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 5px" }}
                />
              </div>
            )}

            {movable.length > 0 && (
              <>
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: T.muted }}>Push out</span>
              <button style={btn} disabled={busy || !chosen.length} onClick={() => applyRelative(7)}>
                +1 week
              </button>
              <button style={btn} disabled={busy || !chosen.length} onClick={() => applyRelative(14)}>
                +2 weeks
              </button>
              <button style={btn} disabled={busy || !chosen.length} onClick={() => applyRelative(28)}>
                +4 weeks
              </button>
            </div>

            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: T.muted }}>or all on</span>
              <input
                type="date"
                value={date}
                min="2000-01-01"
                max="2099-12-31"
                onChange={(e) => setDate(e.target.value)}
                style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 5px" }}
              />
              <button
                style={{ ...btn, background: date && chosen.length ? T.accent : "transparent", color: date && chosen.length ? "#fff" : T.muted, border: "none" }}
                disabled={busy || !date || !chosen.length}
                onClick={() => apply(date)}
              >
                {busy ? "Moving…" : `Move ${chosen.length}`}
              </button>
            </div>
              </>
            )}
            {error && <div style={{ color: T.cost, fontSize: 11, marginTop: 8 }}>{error}</div>}
          </div>
        )}

        {/* Two questions, one list: what is still to happen, and what already
            has. Sorting the settled to the bottom means the top of the list is
            always the part that can still be acted on. */}
        {[...items]
          .sort((a, b) => Number(!!a.isReceived) - Number(!!b.isReceived))
          .map((t) => {
          const canMove = selectable.includes(t);
          const settled = !!t.isReceived;
          return (
            <div
              key={t.key}
              style={{
                borderTop: `1px solid ${T.borderSoft}`,
                padding: "8px 0",
                display: "flex",
                gap: 8,
                alignItems: "center",
                background: settled ? (t.amount >= 0 ? T.receivedSoft : T.settledSoft) : undefined,
              }}
            >
              {readOnly ? null : (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    minWidth: 40,
                    height: 40,
                    margin: "-9px 0",
                    cursor: canMove ? "pointer" : "default",
                  }}
                >
                  <input
                    type="checkbox"
                    disabled={!canMove}
                    checked={!!sel[t.key]}
                    onChange={(e) => setSel({ ...sel, [t.key]: e.target.checked })}
                    style={{ width: 18, height: 18 }}
                  />
                </label>
              )}
              <button
                onClick={() => onOpenOne(t)}
                style={{ flex: 1, textAlign: "left", border: "none", background: "none", cursor: "pointer", font: "inherit", padding: 0 }}
              >
                {settled && (
                  <span
                    style={{ color: t.amount >= 0 ? T.received : T.settled, marginRight: 5, fontWeight: 700 }}
                    title={t.amount >= 0 ? "Received" : "Paid"}
                  >
                    ✓
                  </span>
                )}
                <span style={{ fontSize: 12, color: settled ? T.muted : T.ink }}>{t.label}</span>
                {t.source?.docNo && (
                  <span style={{ fontSize: 10, color: T.muted, marginLeft: 6, fontFamily: fontMono }}>
                    #{t.source.docNo}
                  </span>
                )}
                {t.source?.daysLate > 0 && (
                  <span style={{ fontSize: 10, color: T.cost, marginLeft: 6 }}>
                    {t.source.daysLate}d late
                  </span>
                )}
              </button>
              <span
                style={{
                  fontFamily: fontMono,
                  fontSize: 12,
                  color: settled ? (t.amount >= 0 ? T.received : T.settled) : t.amount >= 0 ? T.income : T.cost,
                }}
              >
                {fmtMoney(t.amount)}
                {settled && (
                  <span style={{ fontSize: 10, display: "block", textAlign: "right", opacity: 0.8 }}>
                    {t.amount >= 0 ? "received" : "paid"}
                    {t.source?.paidDate ? ` ${fmtDay(t.source.paidDate)}` : ""}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * One button for anything that isn't a document.
 *
 * HMRC, a loan repayment, a refund — all of it lived two tabs away under a
 * heading you had to already know about. The forecast is where you notice
 * something is missing, so it should be where you can add it.
 */
function QuickAdd({ openingDate, onClose, onAdded }) {
  const [f, setF] = useState({
    label: "",
    direction: "out",
    category: "other",
    subtype: "",
    amount: "",
    entry_date: todayStr(),
    note: "",
    received: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const PRESETS = [
    { label: "HMRC", category: "tax", subtypes: ["PAYE", "VAT", "CT"] },
    { label: "Lending", category: "lending", direction: "in" },
    { label: "FlexiPay", category: "debt" },
    { label: "Expenses", category: "expenses" },
  ];

  const save = async () => {
    if (!f.label.trim() || !Number(f.amount)) return;
    if (f.category === "lending" && !f.lender?.trim()) {
      setError("Say who the lending is from.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await addManualEntry({
        // Lending is filed under whoever lent it, so the grid shows "Beeches"
        // rather than a row called Lending that could be anyone.
        label:
          f.category === "lending" && f.lender?.trim() ? f.lender.trim() : f.label.trim(),
        category: f.category,
        subtype: f.subtype || null,
        direction: f.direction,
        amount: Number(f.amount),
        entry_date: f.entry_date,
        note: f.note || null,
        received: f.received,
        own_row: true,
      });
      await onAdded();
      onClose();
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  const lbl = { fontSize: 11, color: T.muted };
  const preset = PRESETS.find((p) => p.label === f.label);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(26,24,21,0.35)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: 80,
        zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: T.panel, borderRadius: 12, padding: 20, width: "min(560px,94%)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2 style={{ fontFamily: fontDisplay, fontSize: 20, margin: 0 }}>Add a payment</h2>
          <button
            onClick={onClose}
            style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: T.muted }}
          >
            ×
          </button>
        </div>

        <div style={{ display: "flex", gap: 6, margin: "12px 0", flexWrap: "wrap" }}>
          {PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() =>
                setF({
                  ...f,
                  label: p.label,
                  category: p.category,
                  direction: p.direction || "out",
                  subtype: p.subtypes ? p.subtypes[0] : "",
                })
              }
              style={{
                border: `1px solid ${f.label === p.label ? T.accent : T.border}`,
                background: f.label === p.label ? T.accentSoft : "transparent",
                borderRadius: 999,
                padding: "4px 12px",
                fontSize: 11,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={lbl}>
            Row it appears on
            <br />
            <input
              value={f.label}
              onChange={(e) => setF({ ...f, label: e.target.value })}
              placeholder="HMRC"
              style={{ fontSize: 12, padding: "5px 6px", width: 140, marginTop: 2 }}
            />
          </label>
          {/* Who the money is from. A "Lending" row that does not say whose it
              is tells you nothing you can act on. */}
          {f.category === "lending" && (
            <label style={lbl}>
              From whom
              <br />
              <input
                value={f.lender || ""}
                onChange={(e) => setF({ ...f, lender: e.target.value })}
                placeholder="Beeches"
                style={{ fontSize: 12, padding: "5px 6px", marginTop: 2, width: 150 }}
              />
            </label>
          )}
          {preset?.subtypes && (
            <label style={lbl}>
              Type
              <br />
              <select
                value={f.subtype}
                onChange={(e) => setF({ ...f, subtype: e.target.value })}
                style={{ fontSize: 12, padding: "5px 6px", marginTop: 2 }}
              >
                {preset.subtypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label style={lbl}>
            In or out
            <br />
            <select
              value={f.direction}
              onChange={(e) => setF({ ...f, direction: e.target.value })}
              style={{ fontSize: 12, padding: "5px 6px", marginTop: 2 }}
            >
              <option value="out">Money out</option>
              <option value="in">Money in</option>
            </select>
          </label>
          <label style={lbl}>
            Amount
            <br />
            <input
              value={f.amount}
              onChange={(e) => setF({ ...f, amount: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", width: 100, marginTop: 2 }}
            />
          </label>
          <label style={lbl}>
            Date
            <br />
            <input
              type="date"
              value={f.entry_date}
              onChange={(e) => setF({ ...f, entry_date: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 6px", marginTop: 2 }}
            />
          </label>
          <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 5 }}>
            <input
              type="checkbox"
              checked={f.received}
              onChange={(e) => setF({ ...f, received: e.target.checked })}
            />
            {f.direction === "out" ? "already paid" : "already received"}
          </label>
        </div>

        <label style={{ ...lbl, display: "block", marginTop: 10 }}>
          Note
          <br />
          <input
            value={f.note}
            onChange={(e) => setF({ ...f, note: e.target.value })}
            placeholder="Q2 VAT return"
            style={{ fontSize: 12, padding: "5px 6px", width: "100%", marginTop: 2 }}
          />
        </label>

        {openingDate && f.entry_date <= openingDate && (
          <div style={{ fontSize: 11, color: T.warn, marginTop: 10, lineHeight: 1.5 }}>
            This date is on or before the opening balance ({fmtDay(openingDate)}), so it won't
            appear in the forecast — that money is already inside the opening figure. It will still
            be recorded.
          </div>
        )}

        {error && <div style={{ color: T.cost, fontSize: 11, marginTop: 8 }}>{error}</div>}

        <button
          onClick={save}
          disabled={busy || !f.label.trim() || !Number(f.amount)}
          style={{
            marginTop: 14,
            padding: "8px 18px",
            borderRadius: 6,
            border: "none",
            background: f.label.trim() && Number(f.amount) ? T.accent : T.border,
            color: f.label.trim() && Number(f.amount) ? "#fff" : T.muted,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {busy ? "Adding…" : "Add to forecast"}
        </button>
      </div>
    </div>
  );
}

export default function App({ session }) {
  const [tab, setTab] = useState(tabFromPath);

  // The address bar follows the tab, and the back button follows the address
  // bar. Without the second half, back would leave the app entirely from any
  // tab but the first.
  useEffect(() => {
    const path = TAB_PATHS[tab] || "/";
    if (window.location.pathname.replace(/\/+$/, "") !== path.replace(/\/+$/, "")) {
      window.history.pushState({ tab }, "", path);
    }
  }, [tab]);

  useEffect(() => {
    const onPop = () => setTab(tabFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [picked, setPicked] = useState(null);
  const [focused, setFocused] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMenu, setExportMenu] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const closeDrawer = useCallback(() => {
    setFocused(null);
    setPicked(null);
  }, []);
  const [quickAdd, setQuickAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Seeded from this browser so the grid can draw immediately, then replaced by
  // the shared row as soon as it loads. localStorage is only a cache now.
  const [openingBalance, setOpeningBalance] = useState(() => {
    const v = localStorage.getItem("v2_opening_balance");
    return v == null ? "" : v;
  });
  const [openingDate, setOpeningDate] = useState(
    () => localStorage.getItem("v2_opening_date") || todayStr()
  );
  const [openingSaveError, setOpeningSaveError] = useState(null);
  const [overdraft, setOverdraft] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  // How many weeks of the past to show. One by default: a forecast is about
  // what is coming, and the weeks behind it are context rather than the point.
  const [lookBack, setLookBack] = useState(1);
  const [me, setMe] = useState(null);
  // Read-only until proven otherwise. The database enforces this too; the point
  // here is not to offer controls that would only fail.
  const canEdit = me?.role === "admin" || me?.role === "user";

  useEffect(() => {
    loadMyProfile().then(setMe).catch(() => setMe({ role: "viewer" }));
  }, []);

  // Someone who is not an admin typing /admin should land on the forecast
  // rather than on a tab that renders nothing.
  useEffect(() => {
    if (tab === "admin" && me && me.role !== "admin") setTab("cashflow");
  }, [tab, me]);

  const saveOpening = useCallback(async (patch) => {
    setOpeningSaveError(null);
    if (patch.balance !== undefined) {
      setOpeningBalance(patch.balance);
      localStorage.setItem("v2_opening_balance", patch.balance);
    }
    if (patch.date !== undefined) {
      setOpeningDate(patch.date);
      localStorage.setItem("v2_opening_date", patch.date);
    }
    if (patch.overdraft !== undefined) setOverdraft(patch.overdraft);
    if (patch.fxRates !== undefined) await refresh();
    try {
      await saveOpeningPosition(patch);
    } catch (e) {
      setOpeningSaveError(e.message || String(e));
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await loadAll();
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, error: e.message || String(e), data: null });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The shared row wins over this browser's cache, so everyone — including the
  // investor link, which reads the same row server-side — is working from one
  // starting balance.
  const sharedOpening = state.data?.appSettings;
  useEffect(() => {
    if (!sharedOpening) return;
    if (sharedOpening.opening_balance != null) {
      const v = String(sharedOpening.opening_balance);
      setOpeningBalance(v);
      localStorage.setItem("v2_opening_balance", v);
    }
    if (sharedOpening.opening_date) {
      setOpeningDate(sharedOpening.opening_date);
      localStorage.setItem("v2_opening_date", sharedOpening.opening_date);
    }
    if (sharedOpening.overdraft_limit != null) setOverdraft(String(sharedOpening.overdraft_limit));
  }, [sharedOpening]);

  const d = state.data;
  const forecast = useMemo(
    () => buildForecast(d, openingDate, lookBack),
    [d, openingDate, lookBack]
  );

  /**
   * The same forecast with none of the unclosed work in it.
   *
   * Built properly rather than by subtracting the income lines, because taking
   * the income out without taking its costs out is a version of events nobody
   * is forecasting: if the work does not happen, the media is not bought and
   * the freelancers are not booked. Removing the quote removes what hangs off
   * it, and the card is recalculated too — spend that would have gone on AMEX
   * is not repaid later either.
   */
  const withoutUnclosed = useMemo(() => {
    if (!d || !forecast) return null;
    const unclosed = (forecast.txns || []).filter(
      (t) =>
        t.amount > 0 && !t.isReceived && !t.isHistory &&
        ["scoro_quote", "manual_entry"].includes(t.source?.type)
    );
    if (!unclosed.length) return null;

    const quoteIds = new Set(
      unclosed.filter((t) => t.source.type === "scoro_quote").map((t) => String(t.source.id))
    );
    const manualIds = new Set(
      unclosed.filter((t) => t.source.type === "manual_entry").map((t) => String(t.source.id))
    );
    // Everything being taken out, named the way a linked cost names it.
    const gone = new Set([
      ...[...quoteIds].map((id) => `scoro_quote:${id}`),
      ...[...manualIds].map((id) => `manual_entry:${id}`),
    ]);

    const stripped = {
      ...d,
      quoteOverrides: {
        ...(d.quoteOverrides || {}),
        ...Object.fromEntries([...quoteIds].map((id) => [id, { quote_id: id, include: false }])),
      },
      manualEntries: (d.manualEntries || []).filter((m) => {
        if (manualIds.has(String(m.id))) return false;
        // A cost that names the work it delivers goes when that work goes.
        // This used to be inferred from a sheet row written into both notes,
        // which covered the income typed in alongside and silently missed the
        // five whose income was a quote.
        if (m.linked_doc && gone.has(m.linked_doc)) return false;
        return true;
      }),
      docSpend: (d.docSpend || []).filter(
        (sp) => !gone.has(`${sp.doc_source}:${sp.doc_id}`)
      ),
    };
    return bucketWeeks(buildForecast(stripped, openingDate, 0), openingBalance, openingDate, 0);
  }, [d, forecast, openingBalance, openingDate]);

  const weeks = useMemo(
    () => bucketWeeks(forecast, openingBalance, openingDate, lookBack),
    [forecast, openingBalance, openingDate, lookBack]
  );

  // The hourly syncs are the normal path; this is for when Scoro or Xero has
  // just changed and the answer should not be "wait until the hour".
  const onSyncNow = useCallback(async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) throw new Error("Not signed in");
      const before = d?.lastSync?.started_at || "";
      // Xero runs alongside Scoro rather than after it. Its endpoint answers
      // only once the sync has finished (about twenty seconds), so it is
      // started first and collected once Scoro is done. A Xero failure is
      // reported, but it must not hide a Scoro sync that worked.
      const xero = fetch("/.netlify/functions/xero-sync-now", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(async (r) => (r.ok ? null : (await r.text()).slice(0, 200) || `Xero sync failed (${r.status})`))
        .catch((e) => e.message || String(e));
      // Same origin, so no preflight — the cross-origin call to the sync was
      // failing on a preflight Netlify answers itself, before any of our CORS
      // handling ran.
      const res = await fetch("/.netlify/functions/sync-now", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status !== 202 && !res.ok) {
        const text = await res.text();
        throw new Error(text.slice(0, 200) || `Sync failed (${res.status})`);
      }
      // Accepted, not finished. Watch the run table rather than holding a
      // request open for a minute of Scoro calls — that is what was reporting
      // "failed to fetch" for syncs that had actually worked.
      let done = null;
      for (let i = 0; i < 40 && !done; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const fresh = await loadAll();
        if (
          fresh.lastSync?.started_at &&
          fresh.lastSync.started_at !== before &&
          (fresh.lastSync.finished_at || fresh.lastSync.ok === false)
        ) {
          done = fresh.lastSync;
          setState({ loading: false, error: null, data: fresh });
        }
      }
      const xeroError = await xero;
      // Reload once more so the Xero mirror and bank reading show up even if
      // Scoro finished first.
      setState({ loading: false, error: null, data: await loadAll() });
      if (done && done.ok === false) throw new Error(done.error || "Scoro sync failed");
      if (xeroError) throw new Error(`Xero: ${xeroError}`);
      setSyncMsg(done ? "Synced" : "Still running — it will land shortly");
      setTimeout(() => setSyncMsg(null), 5000);
    } catch (e) {
      setSyncMsg(e.message || String(e));
    } finally {
      setSyncing(false);
    }
  }, [refresh, d]);

  // How long since the mirror last moved. Hourly, so a few hours is a wobble
  // and half a day is a fault.
  const syncAge = useMemo(() => {
    const at = d?.lastSync?.started_at;
    if (syncMsg) return { stale: syncMsg !== "Synced", label: syncMsg, title: syncMsg };
    if (!at) return { stale: true, label: "No Scoro sync recorded", title: "The mirror has never been filled." };
    const at12 = new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const hours = (Date.now() - new Date(at).getTime()) / 3600000;
    const failed = d.lastSync.ok === false;
    if (failed) {
      return {
        stale: true,
        label: `Scoro sync FAILED ${fmtDay(at)} at ${at12}`,
        title: "The last run errored. Figures are as at the last good sync.",
      };
    }
    if (hours > 3) {
      return {
        stale: true,
        label: `Scoro not synced for ${hours >= 48 ? `${Math.round(hours / 24)} days` : `${Math.round(hours)} hours`}`,
        title: `Last successful sync ${fmtDay(at)}. It runs hourly, so this is a fault — the figures below are that old.`,
      };
    }
    return {
      stale: false,
      label: `Scoro synced ${fmtDay(at)} at ${at12}`,
      title: hours < 1.5 ? "" : `${Math.round(hours)} hours ago. It runs hourly.`,
    };
  }, [d, syncMsg]);

  /**
   * The same, for Xero.
   *
   * Two mirrors feeding one forecast means two things that can go stale, and a
   * figure is only as trustworthy as the older of them. Kept separate rather
   * than merged into one "last synced" so a fault can be attributed to the
   * system that actually has it.
   */
  /**
   * What the forecast believes is in the account today.
   *
   * Deliberately the same basis as the figure Xero reports — the opening
   * balance plus every movement dated since, whether settled or merely
   * assumed. It is only worth showing beside the real balance, because the
   * difference between the two is the interesting number: it is movement the
   * model has not accounted for.
   */
  const derivedBalance = useMemo(() => {
    if (!forecast?.txns || !openingDate) return null;
    const upTo = todayStr();
    const moved = forecast.txns
      .filter((t) => !t.isHistory && isoDate(t.date) > openingDate && isoDate(t.date) <= upTo)
      .reduce((s, t) => s + t.amount, 0);
    return (Number(openingBalance) || 0) + moved;
  }, [forecast, openingBalance, openingDate]);

  const xeroAge = useMemo(() => {
    const run = d?.lastXeroSync;
    if (!d?.xeroBills?.length && !run) return null; // not connected; say nothing
    const at = run?.started_at;
    if (!at) return { stale: true, label: "No Xero sync recorded", title: "The mirror has never been filled." };
    const at12 = new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const hours = (Date.now() - new Date(at).getTime()) / 3600000;
    if (run.ok === false) {
      return {
        stale: true,
        label: `Xero sync FAILED ${fmtDay(at)} at ${at12}`,
        title: run.error || "The last run errored. Bills are as at the last good sync.",
      };
    }
    if (hours > 3) {
      return {
        stale: true,
        label: `Xero not synced for ${hours >= 48 ? `${Math.round(hours / 24)} days` : `${Math.round(hours)} hours`}`,
        title: `Last successful sync ${fmtDay(at)}. It runs hourly, so this is a fault.`,
      };
    }
    return {
      stale: false,
      label: `Xero synced ${fmtDay(at)} at ${at12}`,
      title: `${run.bill_count ?? "?"} bills mirrored.`,
    };
  }, [d]);

  // Names for the documents a card charge is attached to, so the AMEX view can
  // say which piece of work is driving the spend rather than repeating the
  // supplier's name down the page.
  // Every piece of income, keyed by its document, so a cost can name the work
  // it delivers and say whether that work is certain.
  const incomeIndex = useMemo(() => {
    const out = {};
    for (const t of forecast?.txns || []) {
      if (t.amount < 0) continue;
      const type = t.source?.type;
      if (type !== "scoro_invoice" && type !== "scoro_quote") continue;
      const key = `${type}:${t.source.id}`;
      if (out[key]) continue;
      out[key] = {
        txn: t,
        label: t.label,
        amount: t.amount,
        date: isoDate(t.date),
        docNo: t.source.docNo,
        stage: t.source.stage,
        isPipeline: !!t.isPipeline,
        isAwaitingPo: !!t.isAwaitingPo,
      };
    }

    // Documents that are in the book but produce no income line of their own.
    //
    // A won quote is the case that matters: it deliberately stops forecasting
    // income, because the invoice it became carries the money instead. Costs
    // stay attached to it, and until now those costs said "rides on a quote"
    // without being able to name which — the one thing you would want to know
    // before deciding whether a £14,000 line belongs in the forecast at all.
    for (const q of d?.quotes || []) {
      const key = `scoro_quote:${q.id}`;
      if (out[key]) continue;
      const won = String(q.status) === "completed";
      out[key] = {
        txn: null,
        label: q.company_name || q.quote_name || "Quote",
        amount: (Number(q.sum) || 0) + (Number(q.vat_sum) || 0),
        date: q.estimated_closing_date || q.deadline || q.date,
        docNo: q.no,
        stage: d?.quoteStatuses?.[q.status]?.label || q.status,
        isPipeline: !won,
        isAwaitingPo: false,
        won,
        notForecast: true,
      };
    }
    // Hand-entered income, so a cost attached to it can name what it delivers
    // rather than saying only that it is attached to something.
    for (const m of d?.manualEntries || []) {
      if (m.direction !== "in" || m.merged_into) continue;
      const key = `manual_entry:${m.id}`;
      if (out[key]) continue;
      out[key] = {
        txn: null,
        label: m.label || "Income",
        amount: Number(m.amount) || 0,
        date: m.entry_date,
        docNo: null,
        stage: m.received ? "Received" : "Expected",
        isPipeline: false,
        isAwaitingPo: false,
        notForecast: false,
      };
    }
    for (const i of d?.scoroInvoices || []) {
      const key = `scoro_invoice:${i.id}`;
      if (out[key]) continue;
      out[key] = {
        txn: null,
        label: i.company_name || "Invoice",
        amount: Number(i.outstanding) || 0,
        date: i.deadline,
        docNo: i.no,
        stage: "Invoiced",
        isPipeline: false,
        isAwaitingPo: false,
        notForecast: true,
      };
    }
    return out;
  }, [forecast, d]);

  const docNames = useMemo(() => {
    const out = {};
    for (const q of d?.quotes || []) {
      out[`scoro_quote:${q.id}`] = { client: q.company_name, docNo: q.no, kind: "quote" };
    }
    for (const inv of d?.scoroInvoices || []) {
      out[`scoro_invoice:${inv.id}`] = { client: inv.company_name, docNo: inv.no, kind: "invoice" };
    }
    return out;
  }, [d]);

  // Today's forecast, kept so next week can score it.
  useEffect(() => {
    if (!weeks.length) return;
    let cancelled = false;
    (async () => {
      try {
        await storeForecastSnapshot(weeks);
        const rows = await loadSnapshots();
        if (!cancelled) setSnapshots(rows);
      } catch {
        // Scoring is a nicety; never let it break the page.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks.length]);

  // Which report. The cash flow was the only one for a while; the ageing
  // reports exist because the question "who owes us what, and how late" gets
  // asked far more often than it is worth opening Scoro for.
  const onExport = useCallback(
    async (kind = "cashflow") => {
      setExporting(kind);
      try {
        const mod = await import("./lib/exportExcel");
        if (kind === "cashflow") {
          await mod.exportWorkbook({
            weeks,
            forecast,
            openingBalance: Number(openingBalance) || 0,
            openingDate,
            buildGridRows,
          });
        } else {
          await mod.exportAged({ forecast, direction: kind });
        }
      } catch (e) {
        setState((st) => ({ ...st, error: e.message || String(e) }));
      } finally {
        setExporting(false);
      }
    },
    [weeks, forecast, openingBalance, openingDate]
  );

  // Shown on the menu so the report is worth opening before you open it.
  const outstandingCounts = useMemo(() => {
    const tally = { income: { n: 0, sum: 0, late: 0 }, suppliers: { n: 0, sum: 0, late: 0 } };
    const seen = new Set();
    for (const t of forecast?.txns || []) {
      const src = t.source;
      if (!src || t.isReceived || t.isHistory) continue;
      const side =
        src.type === "scoro_invoice" ? "income" : src.type === "scoro_bill" ? "suppliers" : null;
      if (!side) continue;
      const box = tally[side];
      // Signed, so a credit note reduces the headline rather than inflating it.
      box.sum += side === "income" ? t.amount : -t.amount;
      const id = `${side}:${src.id}`;
      if (seen.has(id)) continue;
      seen.add(id);
      box.n += 1;
      if ((Number(src.daysLate) || 0) > 0) box.late += 1;
    }
    return tally;
  }, [forecast]);

  // Headline figures people actually ask for. A count of documents told you how
  // much data there was, not how much money.
  const headline = useMemo(() => {
    return headlineFigures(d, forecast);
  }, [d, forecast]);

  const onSetExpected = async (type, id, date) => {
    await saveExpectedDate(type, id, date);
    await refresh();
    closeDrawer();
  };
  /**
   * Turn a quote into something the drawer can show. It carries the same shape
   * as a forecast transaction so one drawer serves both, whether or not the
   * quote is currently in the forecast.
   */
  /**
   * Costs sitting on another document for the same piece of work.
   *
   * A quote becomes an invoice and its costs stay attached to the quote. That
   * is correct — they were agreed against it, and the earlier fix keeps them in
   * the forecast — but the invoice then shows nothing attached, which reads as
   * though delivering it costs nothing.
   *
   * The project is the only link between the two, and it is not a safe one to
   * follow automatically: fourteen of forty-two projects carry more than one
   * open invoice, so "the invoice on this project" is often several. Offered,
   * therefore, never applied.
   */
  const relatedSpendFor = (txn) => {
    const src = txn?.source;
    if (src?.type !== "scoro_invoice") return [];
    const invoice = (d?.scoroInvoices || []).find((i) => String(i.id) === String(src.id));
    if (!invoice?.project_id) return [];
    const quoteIds = new Map(
      (d?.quotes || [])
        .filter((q) => String(q.project_id) === String(invoice.project_id))
        .map((q) => [String(q.id), q.no])
    );
    return (d?.docSpend || [])
      .filter((sp) => sp.doc_source === "scoro_quote" && quoteIds.has(String(sp.doc_id)))
      .map((sp) => ({ ...sp, quoteNo: quoteIds.get(String(sp.doc_id)) }));
  };

  const onAmexPaymentChange = async (action, id, patch) => {
    if (!id) throw new Error("This payment has no record to change — it is calculated.");
    if (action === "delete") await deleteAmexPayment(id);
    else await updateAmexPayment(id, patch);
    await refresh();
    closeDrawer();
  };

  const onPaydownChange = async (date, patch) => {
    await setPaydownOverride(date, patch);
    await refresh();
    closeDrawer();
  };

  const onMoveSpend = async (spendId, docSource, docId) => {
    await updateDocSpend(spendId, { doc_source: docSource, doc_id: String(docId) });
    await refresh();
  };

  const openQuote = (q) => {
    const inForecast = forecast?.txns.find((t) => t.source?.type === "scoro_quote" && t.source.id === String(q.id));
    if (inForecast) return setPicked([{ ...inForecast, source: { ...inForecast.source, inForecast: true } }]);
    const full = (Number(q.sum) || 0) + (Number(q.vat_sum) || 0);
    const own = d?.quoteOverrides?.[q.id];
    setPicked([
      {
        key: `quote-${q.id}`,
        date: parseDate(q.estimated_closing_date || q.deadline || q.date) || new Date(),
        amount: full,
        net: Number(q.sum) || 0,
        label: q.company_name || q.quote_name || "Quote",
        source: {
          type: "scoro_quote",
          id: String(q.id),
          docNo: q.no,
          contact: q.company_name,
          quoteName: q.quote_name,
          issued: q.date,
          dueDate: q.deadline,
          stage: d?.quoteStatuses?.[q.status]?.label || q.status,
          // The raw status as well as the label. "Completed" is a name somebody
          // could change in Scoro tomorrow; `completed` is what the engine
          // keys off, and the two must not drift apart.
          statusKey: String(q.status || ""),
          stageColour: d?.quoteStatuses?.[q.status]?.colour,
          fullValue: full,
          probability: Number(own?.probability ?? d?.quoteRules?.[q.status]?.probability ?? 100),
          expectedDate: own?.expected_date || q.estimated_closing_date || q.deadline,
          estimatedClosing: q.estimated_closing_date,
          inForecast: false,
        },
      },
    ]);
  };

  const onQuoteChange = async (quoteId, patch) => {
    // Removing an override hands the quote back to its stage rule rather than
    // pinning it to "excluded" forever.
    if (patch.include === false && !d?.quoteRules?.[
      (d?.quotes || []).find((q) => String(q.id) === String(quoteId))?.status
    ]?.include) {
      await clearQuoteOverride(quoteId);
    } else {
      await saveQuoteOverride(quoteId, patch);
    }
    await refresh();
    closeDrawer();
  };

  /**
   * The interim before Xero: a direct debit goes out today and the forecast
   * keeps expecting it, because Scoro will not know for days.
   */
  const onMarkPaid = async (type, id, date) => {
    if (date) await markDocPaid(type, id, date);
    else await unmarkDocPaid(type, id);
    await refresh();
    closeDrawer();
  };

  /**
   * Change a standing cost, for one occurrence or for good.
   *
   * "Just this month" writes an override against that date; "every month"
   * changes the cost itself. Offering only one of them would either lose the
   * exception or quietly rewrite the pattern to match it.
   */
  const onRecurringChange = async (scope, id, date, amount) => {
    if (scope === "skip") await setRecurringOverride(id, date, { skip: true });
    else if (scope === "reset") await clearRecurringOverride(id, date);
    else if (scope === "once") await setRecurringOverride(id, date, { amount, skip: false });
    else await updateRecurringCost(id, { amount });
    await refresh();
    closeDrawer();
  };

  /**
   * Change a payment plan.
   *
   * While a plan exists it drives the dates and the document's own expected
   * date is ignored, so editing that was doing nothing at all — the plan is the
   * only thing worth changing, and until now there was no way to.
   */
  const onPlanChange = async (what, id, value) => {
    if (what === "remove") await deleteInstalment(id);
    else if (what === "date") await updateInstalment(id, { due_date: value });
    else if (what === "amount") await updateInstalment(id, { amount: value });
    else if (what === "paid") await updateInstalment(id, { paid: value });
    await refresh();
  };

  /**
   * Split a document into instalments.
   *
   * Divided evenly, with the rounding put on the last payment so the plan adds
   * up to the balance exactly rather than to a penny either side of it.
   */
  const onPlanCreate = async (docType, docId, { count, start, every, total }) => {
    const step = every === "week" ? 7 : every === "fortnight" ? 14 : 0;
    const per = Math.round((total / count) * 100) / 100;
    const rows = [];
    for (let i = 0; i < count; i++) {
      const from = parseDate(start);
      const when = step
        ? addDays(from, step * i)
        : new Date(from.getFullYear(), from.getMonth() + i, from.getDate());
      rows.push({
        doc_source: docType,
        doc_id: String(docId),
        due_date: isoDate(when),
        amount: i === count - 1 ? Math.round((total - per * (count - 1)) * 100) / 100 : per,
        note: `agreed plan · ${i + 1} of ${count}`,
      });
    }
    await addInstalments(rows);
    await refresh();
  };

  const onManualEdit = async (id, patch) => {
    await updateManualEntry(id, patch);
    await refresh();
    closeDrawer();
  };

  const onRecurringPaid = async (id, date, paid) => {
    await setRecurringPaid(id, date, paid);
    await refresh();
    closeDrawer();
  };

  const onManualPaid = async (id, received) => {
    await updateManualEntry(id, { received });
    await refresh();
    closeDrawer();
  };

  const onAddUpdate = async (type, id, note) => {
    await addUpdate(type, id, note, session?.user?.email);
    await refresh();
  };

  const tabs = [
    // Money in with no agreed date has no list of its own, so it flags here —
    // on the tab whose figures are the ones it is missing from.
    { id: "cashflow", label: "Cash flow", unplanned: forecast?.excluded?.unplanned?.in?.count || undefined },
    { id: "retainers", label: "Retainers", badge: d?.retainers?.length },
    { id: "quotes", label: "Pipeline", badge: d?.quotes?.length },
    {
      id: "toclose",
      label: "To close",
      badge:
        (forecast?.txns || []).filter(
          (t) =>
            t.amount > 0 && !t.isReceived && !t.isHistory &&
            ["scoro_quote", "manual_entry"].includes(t.source?.type)
        ).length || undefined,
    },
    { id: "onaccount", label: "Money in/out", badge: (d?.onAccount || []).filter((p) => !p.reconciled).length || undefined },
    { id: "costs", label: "Overheads", badge: (d?.recurring || []).filter((r) => r.active).length || undefined },
    { id: "bills", label: "Bills", badge: (d?.xeroBills?.length || d?.scoroBills?.length || 0) + (d?.manualEntries || []).filter((m) => m.direction === "out" && !m.own_row && m.label !== "HMRC" && !m.received && !m.merged_into).length || undefined, unplanned: forecast?.excluded?.unplanned?.out?.count || undefined },
    { id: "amex", label: "AMEX", badge: forecast?.amex?.breaches?.length || undefined },
    { id: "scenarios", label: "Scenarios" },
    { id: "accuracy", label: "Accuracy" },
    ...(me?.role === "admin" ? [{ id: "admin", label: "Admin" }] : []),
    { id: "questions", label: "Questions", badge: (d?.investorComments || []).filter((c) => !c.answer).length || undefined },
    { id: "import", label: "Import" },
  ];

  if (state.loading) {
    return <div style={{ padding: 40, fontFamily: "'Manrope', system-ui", color: T.muted }}>Loading…</div>;
  }

  const thisWeek = weeks[1] || weeks[0];
  const future = weeks.filter((w) => !w.isHistory);
  // The headline reads the next quarter, not the whole horizon.
  //
  // Beyond about ninety days the only income loaded is contracted retainers,
  // because work that far out has not been quoted yet. The line therefore falls
  // away by construction, and a "lowest projected" taken from the far end
  // measures the absence of data rather than the state of the business.
  const lowOf = (ws) => ws.reduce((m, w) => (m == null || w.endBal < m.endBal ? w : m), null);
  const highOf = (ws) => ws.reduce((m, w) => (m == null || w.endBal > m.endBal ? w : m), null);
  // A month answers "is there a problem now"; a quarter answers "is there one
  // coming". Beyond that the only income loaded is contracted retainers, so the
  // line falls away by construction and the far end would measure the absence
  // of data rather than the state of the business.
  const month = future.slice(0, 4);
  const quarter = future.slice(0, 13);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Manrope', system-ui, sans-serif", color: T.ink }}>
      <div style={{ padding: "16px 24px", display: "flex", alignItems: "center", gap: 20 }}>
        <img
          src={syrupLogo}
          alt="Syrup"
          style={{ height: 34, width: "auto", display: "block" }}
        />
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          {/* A sync that stops working is invisible unless it says so: the
              mirror simply stops moving while every figure still looks
              confident. It stayed broken for a day that way. */}
          <div
            style={{
              fontSize: 11,
              padding: syncAge.stale ? "5px 10px" : 0,
              borderRadius: 6,
              background: syncAge.stale ? "#FBEFE2" : "transparent",
              border: syncAge.stale ? `1px solid ${T.warn}` : "none",
              color: syncAge.stale ? T.ink : T.muted,
              fontWeight: syncAge.stale ? 600 : 400,
            }}
            title={syncAge.title}
          >
            {syncAge.label}
          </div>
          {xeroAge && (
            <div
              style={{
                fontSize: 11,
                padding: xeroAge.stale ? "5px 10px" : 0,
                borderRadius: 6,
                background: xeroAge.stale ? "#FBEFE2" : "transparent",
                border: xeroAge.stale ? `1px solid ${T.warn}` : "none",
                color: xeroAge.stale ? T.ink : T.muted,
                fontWeight: xeroAge.stale ? 600 : 400,
              }}
              title={xeroAge.title}
            >
              {xeroAge.label}
            </div>
          )}
          <button
            onClick={onSyncNow}
            disabled={syncing}
            style={{
              padding: "6px 11px",
              borderRadius: 6,
              border: `1px solid ${T.border}`,
              background: "transparent",
              fontSize: 11,
              cursor: syncing ? "default" : "pointer",
              fontFamily: "inherit",
              color: T.muted,
            }}
          >
            {syncing ? "Syncing…" : "Sync now"}
          </button>

          {/* Who is signed in, and the way out.
              There was no way out at all: a session persists, so anyone who
              opened this on a shared or borrowed device stayed signed into the
              company's finances until the token expired on its own. Naming the
              account matters as much as the button — on a shared machine the
              first question is whose session this is. */}
          {session?.user?.email && (
            <>
              <span
                style={{ fontSize: 11, color: T.muted, maxWidth: 190, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={`Signed in as ${session.user.email}${me?.role ? ` · ${me.role}` : ""}`}
              >
                {session.user.email}
              </span>
              <button
                onClick={async () => {
                  // Local first, so a network that refuses still ends the
                  // session on this device — the one place it certainly
                  // matters. Then the global call, best effort.
                  await supabase.auth.signOut({ scope: "local" }).catch(() => {});
                  supabase.auth.signOut().catch(() => {});
                  window.location.replace("/");
                }}
                title="Sign out on this device"
                style={{
                  padding: "6px 11px",
                  borderRadius: 6,
                  border: `1px solid ${T.border}`,
                  background: "transparent",
                  color: T.muted,
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                Sign out
              </button>
            </>
          )}
        </div>
      </div>

      {me && !canEdit && (
        <div
          style={{
            background: "#FBEFE2",
            borderTop: `1px solid ${T.warn}`,
            borderBottom: `1px solid ${T.warn}`,
            padding: "8px 24px",
            fontSize: 12,
          }}
        >
          You have <strong>view-only</strong> access. Everything is visible; nothing can be changed.
        </div>
      )}

      {/* Fourteen tabs wrap into a block four rows deep on a phone, pushing
          the figures below the fold. Sideways is the right direction for a
          row of things. */}
      <nav
        data-scroll-x
        style={{ display: "flex", gap: 18, padding: "0 24px", borderBottom: `1px solid ${T.border}` }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              border: "none",
              background: "none",
              padding: "8px 0 10px",
              cursor: "pointer",
              fontSize: 13,
              fontFamily: "inherit",
              color: tab === t.id ? T.ink : T.muted,
              fontWeight: tab === t.id ? 600 : 400,
              borderBottom: tab === t.id ? `2px solid ${T.ink}` : "2px solid transparent",
            }}
          >
            {t.label}
            {t.badge ? (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  background: T.accentSoft,
                  color: T.accent,
                  borderRadius: 999,
                  padding: "1px 6px",
                }}
              >
                {t.badge}
              </span>
            ) : null}
            {/* Owed, overdue, and with no date anybody has agreed — so not in
                the forecast at all. Amber rather than the usual tint, because
                this is money the grid below is silently not counting. */}
            {t.unplanned ? (
              <span
                title={`${t.unplanned} with no payment date agreed — not in the forecast`}
                style={{
                  marginLeft: 4,
                  fontSize: 10,
                  background: "#FBEFE2",
                  color: "#8A6A1F",
                  border: "1px solid #8A6A1F",
                  borderRadius: 999,
                  padding: "0 5px",
                  fontWeight: 700,
                }}
              >
                {t.unplanned}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      <main style={{ padding: 24 }}>
        {state.error && (
          <div style={{ color: T.cost, fontSize: 12, marginBottom: 12 }}>{state.error}</div>
        )}

        {tab === "cashflow" && (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <KPI
                label="Cash this week"
                value={thisWeek ? fmtMoney(thisWeek.endBal) : "—"}
                sub={thisWeek ? `w/e ${fmtDay(isoDate(thisWeek.end))}` : ""}
                tone={thisWeek && thisWeek.endBal < 0 ? "bad" : undefined}
              />
              <RangeKPI
                label="Next 30 days"
                low={lowOf(month)}
                high={highOf(month)}
                overdraft={Number(d?.appSettings?.overdraft_limit) || 0}
              />
              <RangeKPI
                label="Next 13 weeks"
                low={lowOf(quarter)}
                high={highOf(quarter)}
                overdraft={Number(d?.appSettings?.overdraft_limit) || 0}
              />
              <KPI
                label="Owed to us"
                value={fmtMoney(headline.owed)}
                sub={
                  headline.owedOverdue
                    ? `${fmtMoney(headline.owedOverdue)} of it overdue`
                    : "none overdue"
                }
                tone={headline.owedOverdue > headline.owed / 2 ? "bad" : undefined}
              />
            </div>

            {forecast?.excluded?.nonGbp > 0 && (
              <div
                style={{
                  background: "#FBEFE2",
                  border: `1px solid ${T.warn}`,
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 12,
                  fontSize: 12,
                  lineHeight: 1.6,
                }}
              >
                <strong>
                  {forecast.excluded.nonGbp} document
                  {forecast.excluded.nonGbp === 1 ? " is" : "s are"} not in the forecast
                </strong>{" "}
                because there is no exchange rate for{" "}
                {Object.entries(forecast.excluded.currencies)
                  .map(([c, v]) => `${c} ${Math.round(v).toLocaleString()}`)
                  .join(", ")}
                . Set one in Admin and they come straight in.
              </div>
            )}

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, fontSize: 11, color: T.muted }}>
              <span>Show</span>
              {[1, 4, 13, 26].map((n) => (
                <button
                  key={n}
                  onClick={() => setLookBack(n)}
                  style={{
                    border: `1px solid ${lookBack === n ? T.accent : T.border}`,
                    background: lookBack === n ? T.accentSoft : "transparent",
                    color: lookBack === n ? T.ink : T.muted,
                    borderRadius: 999,
                    padding: "3px 10px",
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {n === 1 ? "last week" : `${n} weeks back`}
                </button>
              ))}
              {lookBack > 1 && (
                <span
                  style={{
                    color: T.ink,
                    background: "#FBEFE2",
                    border: `1px solid ${T.warn}`,
                    borderRadius: 6,
                    padding: "4px 9px",
                    lineHeight: 1.5,
                  }}
                >
                  <strong>Past weeks show money in, not money out.</strong> Scoro is only mirrored
                  for unpaid bills, so nothing that has already been paid is recorded — a past week
                  will look better than it was. The balance is blank before the opening date,
                  because everything earlier is already inside that figure.
                </span>
              )}
            </div>

            <Legend />

            <CashFlowGrid
              weeks={weeks}
              onPick={(txns) => setPicked(txns)}
              spendByDoc={forecast?.spendByDoc || {}}
              overdraftLimit={Number(d?.appSettings?.overdraft_limit) || 0}
              openingDate={openingDate}
              bankByWeekEnd={d?.bankByWeekEnd || {}}
            />

          </>
        )}

        {tab === "retainers" && (
          <RetainersView
            retainers={d?.retainers || []}
            settings={d?.retainerSettings || {}}
            paidInvoices={d?.paidInvoices || []}
            onChanged={refresh}
          />
        )}

        {tab === "quotes" && (
          <QuotesView
            quotes={d?.quotes || []}
            statuses={d?.quoteStatuses || {}}
            rules={d?.quoteRules || {}}
            overrides={d?.quoteOverrides || {}}
            onOpenQuote={openQuote}
            onChanged={refresh}
          />
        )}

        {tab === "onaccount" && (
          <OnAccountView
            invoices={d?.scoroInvoices || []}
            payments={d?.onAccount || []}
            manual={d?.manualEntries || []}
            openingDate={openingDate}
            onChanged={refresh}
          />
        )}

        {showSettings && (
          <div
            onClick={() => setShowSettings(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(26,24,21,0.35)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 90, zIndex: 60 }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: T.panel, borderRadius: 12, padding: 20, width: "min(520px,94%)" }}
            >
              <h2 style={{ fontFamily: fontDisplay, fontSize: 20, marginTop: 0 }}>Opening balance</h2>
              <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                The one figure with no document behind it, and the anchor every closing balance is
                built on. Set it to the bank balance on a date you can reconcile. Anything dated on
                or before it is treated as already inside it — settled items from the four weeks
                before still show on the grid in grey, for context, but never count twice.
              </p>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginTop: 12 }}>
                <label style={{ fontSize: 11, color: T.muted }}>
                  Balance
                  <br />
                  <input
                    value={openingBalance}
                    onChange={(e) => saveOpening({ balance: e.target.value })}
                    placeholder="-47074"
                    style={{ fontFamily: fontMono, fontSize: 13, padding: "6px 8px", width: 140, marginTop: 2 }}
                  />
                </label>
                <label style={{ fontSize: 11, color: T.muted }}>
                  Overdraft
                  <br />
                  <input
                    value={overdraft}
                    onChange={(e) => saveOpening({ overdraft: e.target.value })}
                    placeholder="50000"
                    style={{ fontFamily: fontMono, fontSize: 13, padding: "6px 8px", width: 110, marginTop: 2 }}
                  />
                </label>
                <label style={{ fontSize: 11, color: T.muted }}>
                  as at
                  <br />
                  <input
                    type="date"
                    value={openingDate}
                    onChange={(e) => saveOpening({ date: e.target.value })}
                    style={{ fontFamily: fontMono, fontSize: 13, padding: "6px 8px", marginTop: 2 }}
                  />
                </label>
                <button
                  onClick={() => setShowSettings(false)}
                  style={{ marginLeft: "auto", padding: "8px 16px", borderRadius: 6, border: "none", background: T.accent, color: "#fff", fontSize: 12, cursor: "pointer" }}
                >
                  Done
                </button>
              </div>
              <div style={{ fontSize: 11, color: openingSaveError ? T.cost : T.muted, marginTop: 12, lineHeight: 1.5 }}>
                {openingSaveError
                  ? `Not saved: ${openingSaveError}`
                  : "Shared with everyone using the tool, including the investor link, so the forecast starts from the same cash position wherever it is opened."}
              </div>
            </div>
          </div>
        )}

        {quickAdd && <QuickAdd openingDate={openingDate} onClose={() => setQuickAdd(false)} onAdded={refresh} />}

        {tab === "bills" && (
          <BillsView
            scoroBills={d?.scoroBills || []}
            xeroBills={d?.xeroBills || []}
            instalments={d?.instalments || []}
            decisions={d?.paymentDecisions || {}}
            session={session}
            manualEntries={d?.manualEntries || []}
            expectedDates={d?.expectedDates || {}}
            paidMarks={d?.paidMarks || {}}
            dismissals={d?.dismissals || {}}
            onChanged={refresh}
          />
        )}

        {tab === "amex" && (
          <AmexView
            settings={d?.amexSettings}
            payments={d?.amexPayments || []}
            amex={forecast?.amex}
            weeks={weeks}
            charges={forecast?.charges || []}
            docNames={docNames}
            overdraftLimit={Number(d?.appSettings?.overdraft_limit) || 0}
            onChanged={refresh}
          />
        )}

        {tab === "costs" && <CostsView recurring={d?.recurring || []} onChanged={refresh} />}

        {tab === "scenarios" && (
          <ScenariosView
            forecast={forecast}
            weeks={weeks}
            openingBalance={Number(openingBalance) || 0}
            openingDate={openingDate}
            overdraftLimit={Number(d?.appSettings?.overdraft_limit) || 0}
          />
        )}

        {tab === "accuracy" && (
          <AccuracyView snapshots={snapshots} weeks={weeks} />
        )}

        {tab === "admin" && me?.role === "admin" && (
          <AdminView
            me={me}
            openingBalance={openingBalance}
            openingDate={openingDate}
            overdraft={overdraft}
            fxRates={d?.appSettings?.fx_rates || {}}
            excluded={forecast?.excluded}
            onSaveOpening={saveOpening}
            openingError={openingSaveError}
            onChanged={refresh}
            derivedBalance={derivedBalance}
          />
        )}

        {tab === "questions" && (
          <QuestionsView comments={d?.investorComments || []} onChanged={refresh} />
        )}

        {tab === "toclose" && (
          <ToCloseView
            forecast={forecast}
            weeks={weeks}
            weeksWithout={withoutUnclosed}
            openingBalance={Number(openingBalance) || 0}
            onOpen={(txn) => setPicked([txn])}
          />
        )}

        {tab === "import" && <ImportView onImported={refresh} />}

        {/* Always within reach, whatever is on screen and however far down the
            grid you have scrolled. */}
        {canEdit && tab === "cashflow" && (
          <div
            style={{
              position: "fixed",
              right: 22,
              bottom: 22,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              zIndex: 40,
            }}
          >
            {exportMenu && (
              <ExportMenu
                busy={exporting}
                counts={outstandingCounts}
                onPick={(kind) => {
                  setExportMenu(false);
                  onExport(kind);
                }}
                onClose={() => setExportMenu(false)}
              />
            )}
            <button
              onClick={() => setExportMenu((v) => !v)}
              disabled={!!exporting}
              title="Export to Excel"
              aria-label="Export to Excel"
              aria-expanded={exportMenu}
              style={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                border: `1px solid ${T.border}`,
                background: T.panel,
                color: T.muted,
                fontSize: 17,
                cursor: exporting ? "default" : "pointer",
                boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
              }}
            >
              {exporting ? (
                "…"
              ) : (
                // A spreadsheet, not a download arrow — the arrow read as
                // "scroll down" sitting at the foot of a long grid.
                <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                  <rect x="2.5" y="3" width="15" height="14" rx="2" fill="#1D6F42" />
                  <path d="M2.5 8h15M2.5 12.5h15M8 3v14M13 3v14" stroke="#fff" strokeWidth="1.1" opacity="0.85" />
                </svg>
              )}
            </button>
            <button
              onClick={() => setQuickAdd(true)}
              title="Add a payment"
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                border: "none",
                background: T.accent,
                color: "#fff",
                fontSize: 26,
                lineHeight: 1,
                cursor: "pointer",
                boxShadow: "0 3px 12px rgba(0,0,0,0.18)",
              }}
            >
              +
            </button>
          </div>
        )}

        {/* Outside the tabs on purpose. A document can be reached from the
            grid, the pipeline, the bills list or a search, and a drawer that
            only renders on the cash flow tab silently does nothing from any
            of the others. */}
              {picked && picked.length > 1 && !focused && (
                <BulkPanel
                  items={picked}
                  onClose={() => setPicked(null)}
                  onOpenOne={(t) => setFocused(t)}
                  onMoved={async () => {
                    await refresh();
                    setPicked(null);
                  }}
                />
              )}

              {(focused || picked?.length === 1) && (() => {
                const one = focused || picked[0];
                const key = `${one.source?.type}:${one.source?.id}`;
                const cameFromList = !!focused && picked && picked.length > 1;
                return (
                  <DocumentDrawer
                    txn={one}
                    session={session}
                    updates={d?.updatesByDoc?.[key]}
                    spend={forecast?.spendByDoc?.[key]}
                    relatedSpend={relatedSpendFor(one)}
                    onMoveSpend={onMoveSpend}
                    onPaydownChange={onPaydownChange}
                    onAmexPaymentChange={onAmexPaymentChange}
                    amexUnconfigured={!!forecast?.amex?.usingDefaults}
                    plan={(d?.instalments || []).filter(
                      (i) => i.doc_source === one.source?.type && String(i.doc_id) === String(one.source?.id)
                    )}
                    onSpendChanged={refresh}
                    linkedDoc={
                      one.source?.attachedTo
                        ? incomeIndex[`${one.source.attachedTo.source}:${one.source.attachedTo.id}`]
                        : null
                    }
                    onOpenDoc={(t) => setFocused(t)}
                    onBack={cameFromList ? () => setFocused(null) : undefined}
                    backCount={cameFromList ? picked.length : undefined}
                    onClose={closeDrawer}
                    onSetExpected={onSetExpected}
                    onAddUpdate={onAddUpdate}
                    onQuoteChange={onQuoteChange}
                    onMarkPaid={onMarkPaid}
                    onManualPaid={onManualPaid}
                    onManualEdit={onManualEdit}
                    onPlanChange={onPlanChange}
                    onPlanCreate={onPlanCreate}
                    onRecurringChange={onRecurringChange}
                    onRecurringPaid={onRecurringPaid}
                  readOnly={!canEdit}
                  />
                );
              })()}
      </main>
    </div>
  );
}


// The investor link renders the same drawer, read-only.
export { DocumentDrawer, BulkPanel };
